import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Alert,
  Button,
  Card,
  Carousel,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  InfoCircleOutlined,
  RiseOutlined,
  FallOutlined,
  PlusOutlined,
  MinusOutlined,
  EditOutlined,
  DeleteOutlined,
  InboxOutlined,
  NodeIndexOutlined,
  AreaChartOutlined,
} from "@ant-design/icons";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import dayjs from "dayjs";
import {
  getAllCashFlowItems,
  createCashFlowItem,
  updateCashFlowItem,
  deleteCashFlowItem,
  CashFlowFormData as APICashFlowFormData,
  getCashFlowHistory,
  logCashFlowHistoryEvent,
  getCashFlowSyncMetadata,
  getCashFlowSyncRules,
  createCashFlowSyncRule,
  updateCashFlowSyncRule,
  deleteCashFlowSyncRule,
  getCashFlowSyncProposals,
  applyCashFlowSyncProposals,
  CashFlowSyncRule as APICashFlowSyncRule,
  CashFlowSyncProposal as APICashFlowSyncProposal,
  CashFlowSyncMetadataModel as APICashFlowSyncMetadataModel,
  previewCashFlowSyncExample,
} from "../../services/cashFlowService";
import { useAppDispatch, useAppSelector } from "../../store";
import { setCurrentBalance, setInitialBalance } from "../../store/cashFlowSlice";

import Finance360 from "./Finance360";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Import des types depuis le service cashFlow
import {
  CashFlowItemType,
  CashFlowItem as APICashFlowItem,
} from "../../services/cashFlowService";
import "../../styles/finance.css";

// Interface adaptée pour le composant Finance, compatible avec l'API
interface CashFlowItem extends Omit<APICashFlowItem, "type"> {
  id?: string; // Pour compatibilité avec le code existant (correspond à _id de l'API)
  type: CashFlowItemType; // Type d'opération (recurrent ou ponctuel)
  categorie: string; // Catégorie côté front (compatibilité avec le code existant)
}

// Formatage de l'échéance pour les opérations (sans préciser le mois pour les récurrentes)
const formatEcheance = (item: CashFlowItem): string => {
  if (item.type === CashFlowItemType.RECURRENT || item.jourDuMois) {
    const jourDuMois = item.jourDuMois || 1;
    return `Prélèvement le ${jourDuMois}`;
  } else if (item.echeance) {
    return dayjs(item.echeance).format("DD/MM/YYYY");
  } else if (item.dateDebut) {
    return dayjs(item.dateDebut).format("DD/MM/YYYY");
  } else {
    return "Date non spécifiée";
  }
};

const getItemReferenceDate = (item: CashFlowItem): dayjs.Dayjs | null => {
  if (item.type === CashFlowItemType.RECURRENT && item.jourDuMois) {
    const today = dayjs();
    const monthStart = today.startOf("month");
    const monthEnd = today.endOf("month");
    const targetDay =
      item.jourDuMois > monthEnd.date() ? monthEnd.date() : item.jourDuMois;
    return monthStart.date(targetDay);
  }
  if (item.echeance) {
    return dayjs(item.echeance);
  }
  if (item.dateDebut) {
    return dayjs(item.dateDebut);
  }
  return null;
};

const getItemMonthKey = (item: CashFlowItem): string => {
  const ref = getItemReferenceDate(item);
  return ref ? ref.format("YYYY-MM") : "9999-12";
};

const getItemMonthLabel = (item: CashFlowItem): string => {
  const ref = getItemReferenceDate(item);
  return ref ? ref.format("MMMM YYYY") : "Sans mois";
};

// Fonction pour regrouper par catégorie (adaptée pour gérer les données de l'API)
const groupByCategory = (
  items: CashFlowItem[],
  selectedItems: Record<string, boolean> = {}
) => {
  const result = {
    depense_recurrente: [] as CashFlowItem[],
    depense_vente: [] as CashFlowItem[],
    revenu_vente: [] as CashFlowItem[],
    depense_exceptionnelle: [] as CashFlowItem[],
    revenu_exceptionnel: [] as CashFlowItem[],
  };

  const today = dayjs();

  items.forEach((item) => {
    // Vérifier si l'item a un ID et s'il est sélectionné (ou pas encore dans selectedItems)
    if (item.id && selectedItems[item.id] === false) {
      // Skip les items désélectionnés
      return;
    }

    // Pour les cashflows ponctuels, ne pas afficher ceux dont l'échéance est passée
    if (item.type === CashFlowItemType.PONCTUEL && item.echeance) {
      const echeanceDate = dayjs(item.echeance);
      if (echeanceDate.isBefore(today, "day")) {
        // Skip les items ponctuels dont l'échéance est passée
        return;
      }
    }

    // Pour les cashflows récurrents, ne pas afficher ceux dont la date de fin est passée
    if (item.type === CashFlowItemType.RECURRENT && item.dateFin) {
      const dateFinRecurrent = dayjs(item.dateFin);
      if (dateFinRecurrent.isBefore(today, "day")) {
        // Skip les items récurrents dont la date de fin est passée
        return;
      }
    }

    if (item.categorie && item.categorie in result) {
      result[item.categorie as keyof typeof result].push(item);
    } else {
      console.warn(`Catégorie inconnue ou manquante: ${item.categorie}`);
    }
  });

  return result;
};

// Types pour les données du graphique
interface BalanceDataPoint {
  date: string;
  balance: number;
}

// Interface pour les props du CustomTooltip
interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

// Interface pour les items TVA
interface TVAItem {
  id: string;
  libelle: string;
  montantTTC: number;
  montantHT: number;
  tauxTVA: number;
  tva: number;
  isRevenue: boolean;
  date: string;
  mois: string;
}

// Interface pour les données mensuelles TVA
interface TVAMonthData {
  mois: string; // Libellé "Décembre 2025"
  monthKey: string; // Clé stable pour la date, ex: "2025-12-01"
  revenus: TVAItem[];
  depenses: TVAItem[];
  soldeTVA: number;
  overrideSoldeTVA?: number;
  // Propriétés pour les totaux calculés
  totalRevenus?: number;
  totalDepenses?: number;
  totalTVA?: number;
}

type TVAOverrides = Record<string, number>;

interface CashFlowHistoryEvent {
  id: string;
  type:
    | "create"
    | "update"
    | "delete"
    | "archive"
    | "echeance_update"
    | "tva_override"
    | "initial_balance_change"
    | "ticket_sync";
  timestamp: string;
  title: string;
  details: string;
  metadata?: any;
}

const HISTORY_WINDOW_DAYS = 10;

// const formatTimeAgo = (timestamp: string): string => {
//   const now = dayjs();
//   const time = dayjs(timestamp);
//   const diffMinutes = now.diff(time, "minute");

//   if (diffMinutes < 60) {
//     const m = Math.max(diffMinutes, 1);
//     return `il y a ${m} min`;
//   }

//   const diffHours = now.diff(time, "hour");
//   if (diffHours < 24) {
//     return `il y a ${diffHours} h`;
//   }

//   const diffDays = now.diff(time, "day");
//   return `il y a ${diffDays} jour${diffDays > 1 ? "s" : ""}`;
// };

// Résumé lisible de ce qui a changé pour un événement d'historique
const getHistoryChangeSummary = (event: CashFlowHistoryEvent): string => {
  switch (event.type) {
    case "create":
      return "Nouvelle opération";
    case "delete":
      return "Opération supprimée";
    case "archive":
      return "Fin de la récurrence";
    case "echeance_update":
      return "Date d'échéance modifiée";
    case "tva_override":
      return "Solde de TVA ajusté";
    case "initial_balance_change":
      return "Solde initial modifié";
    case "update": {
      const fields: string[] = [];
      const d = event.details;

      if (d.includes("Libellé :")) fields.push("libellé");
      if (d.includes("Catégorie :")) fields.push("catégorie");
      if (d.includes("Sens :")) fields.push("sens");
      if (d.includes("Type :")) fields.push("type");
      if (d.includes("Montant TTC :")) fields.push("montant TTC");
      if (d.includes("Montant HT :")) fields.push("montant HT");
      if (d.includes("TVA :")) fields.push("TVA");
      if (d.includes("Taux TVA :")) fields.push("taux de TVA");
      if (d.includes("Échéance :")) fields.push("échéance");
      if (d.includes("Jour de prélèvement")) fields.push("jour de prélèvement");

      if (fields.length === 0) return "Opération modifiée";
      if (fields.length === 1) return `Changement de ${fields[0]}`;
      if (fields.length === 2)
        return `Changements sur ${fields[0]} et ${fields[1]}`;
      return `Changements sur ${fields[0]}, ${fields[1]}…`;
    }
    default:
      return "";
  }
};

// Formatage d'un montant en euros
const formatCurrency = (value: number | undefined | null): string => {
  if (typeof value !== "number") {
    return "-";
  }
  return `${value.toLocaleString("fr-FR")} €`;
};

// Formatage court d'une date à partir de labels connus (JJ/MM/AAAA ou ISO)
const formatShortDateLabel = (raw: string | undefined | null): string => {
  if (!raw) return "-";
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "(aucune)") return trimmed || "-";

  // D'abord tenter JJ/MM/AAAA
  const parsedNumeric = dayjs(trimmed, "DD/MM/YYYY", true);
  if (parsedNumeric.isValid()) {
    return parsedNumeric.format("DD MMM YYYY");
  }

  // Sinon tenter un format ISO/Date classique
  const parsed = dayjs(trimmed);
  if (parsed.isValid()) {
    return parsed.format("DD MMM YYYY");
  }

  return trimmed;
};

// Déterminer les types de changements dans un détail d'update
// const getUpdateChangeKinds = (details: string) => {
//   return {
//     montantTTC: details.includes("Montant TTC :"),
//     echeance: details.includes("Échéance :"),
//     jourPrelevement: details.includes("Jour de prélèvement"),
//     tvaRate: details.includes("Taux TVA :"),
//     tvaAmount: details.includes("TVA :"),
//   };
// };

// Sous-titre des cartes: toujours présent
// - Tickets: "Nom du ticket | ponctuel ou récurrent"
// - TVA: mois et année concernés
// - Autres: fallback sur le titre de l'évènement
const getHistoryCardSubtitle = (event: CashFlowHistoryEvent): string => {
  const metadata = event.metadata || {};

  // Cas spécifique: ajustement TVA -> afficher le mois/année concernés
  if (event.type === "tva_override") {
    const monthLabel = metadata.monthLabel as string | undefined;
    if (monthLabel) {
      return `TVA ${monthLabel}`;
    }
    return "TVA";
  }

  // Cas spécifique: modification de solde initial
  if (event.type === "initial_balance_change") {
    return "Solde initial";
  }

  const label = (metadata.itemLabel as string | undefined) || undefined;
  const rawType = metadata.itemType as CashFlowItemType | string | undefined;

  let typeLabel: string | null = null;
  if (rawType === CashFlowItemType.RECURRENT || rawType === "recurrent") {
    typeLabel = "récurrent";
  } else if (
    rawType === CashFlowItemType.PONCTUEL ||
    rawType === "ponctuel"
  ) {
    typeLabel = "ponctuel";
  }

  if (label && typeLabel) {
    return `${label} | ${typeLabel}`;
  }
  if (label) return label;
  if (typeLabel) return typeLabel;

  // Fallback: au minimum, on affiche le titre existant
  return event.title;
};

// Titre des cartes en fonction de ce qui a changé
// const formatHistoryCardTitle = (event: CashFlowHistoryEvent): string => {
//   if (event.type === "update") {
//     if (
//       event.title === "Modification du montant" ||
//       event.title === "Modification de la TVA" ||
//       event.title === "Modification du jour de prélèvement"
//     ) {
//       return event.title;
//     }

//     const kinds = getUpdateChangeKinds(event.details);
//     const keys: string[] = [];
//     if (kinds.montantTTC) keys.push("montantTTC");
//     if (kinds.echeance) keys.push("echeance");
//     if (kinds.jourPrelevement) keys.push("jourPrelevement");
//     if (kinds.tvaRate || kinds.tvaAmount) keys.push("tva");

//     if (keys.length === 1) {
//       switch (keys[0]) {
//         case "montantTTC":
//           return "Modification du montant";
//         case "echeance":
//           return "Modification de la date d'échéance";
//         case "jourPrelevement":
//           return "Modification du jour de prélèvement";
//         case "tva":
//           return "Modification TVA";
//       }
//     }
//   }

//   if (event.type === "create") {
//     return "Ajout de ticket";
//   }
//   if (event.type === "delete") {
//     return "Suppression de ticket";
//   }
//   if (event.type === "echeance_update") {
//     return "Modification de la date d'échéance";
//   }
//   if (event.type === "tva_override") {
//     return "Modification TVA";
//   }

//   // Fallback: garder le titre existant
//   return event.title;
// };

// // Description principale de la carte (ligne de détail agrandie)
// const formatHistoryCardDescription = (
//   event: CashFlowHistoryEvent
// ): string => {
//   const metadata = event.metadata || {};
//   const d = event.details || "";

//   if (event.type === "create") {
//     const cat = (metadata.categorie as string | undefined) || undefined;
//     const taux = metadata.tauxTVA as number | undefined;
//     const rawType = metadata.itemType as CashFlowItemType | string | undefined;
//     const jour = metadata.jourDuMois as number | undefined;
//     const echeance = metadata.echeance as string | undefined;
//     const amount = metadata.montantTTC as number | undefined;
    

//     const parts: string[] = [];
//     if (typeof amount === "number") parts.push(`${amount.toFixed(2)}€`);
//     if (cat) parts.push(`${cat}`);
//     if (typeof taux === "number") parts.push(`${taux}%`);
//     const isRecurrent =
//       rawType === CashFlowItemType.RECURRENT || rawType === "recurrent";

//     if (isRecurrent) {
//       if (jour) {
//         parts.push(`${jour} jour du mois`);
//       }
//     } else if (echeance) {
//       parts.push(`${formatShortDateLabel(echeance)}`);
//     }

//     if (parts.length > 0) {
//       return parts.join(" • ");
//     }
//   }

//   if (event.type === "update") {
//     // Cas dédiés pour les modifications ciblées que l'on génère nous-mêmes
//     if (event.title === "Modification du montant") {
//       const match = d.match(/([^€]+€)\s*->\s*([^€]+€)/);
//       if (match) {
//         return `${match[1].trim()} -> ${match[2].trim()}`;
//       }
//     }

//     if (event.title === "Modification de la TVA") {
//       const match = d.match(/([^%]+%)\s*->\s*([^%]+%)/);
//       if (match) {
//         return `${match[1].trim()} -> ${match[2].trim()}`;
//       }
//     }

//     if (event.title === "Modification du jour de prélèvement") {
//       // Pour ce cas, on renvoie directement le détail "2 -> 8 jour du mois"
//       return d;
//     }

//     const kinds = getUpdateChangeKinds(d);
//     const onlyAmount =
//       kinds.montantTTC &&
//       !kinds.echeance &&
//       !kinds.jourPrelevement &&
//       !kinds.tvaRate &&
//       !kinds.tvaAmount;
//     const onlyEcheance =
//       kinds.echeance &&
//       !kinds.montantTTC &&
//       !kinds.jourPrelevement &&
//       !kinds.tvaRate &&
//       !kinds.tvaAmount;
//     const onlyJour =
//       kinds.jourPrelevement &&
//       !kinds.montantTTC &&
//       !kinds.echeance &&
//       !kinds.tvaRate &&
//       !kinds.tvaAmount;
//     const onlyTvaRate =
//       (kinds.tvaRate || kinds.tvaAmount) &&
//       !kinds.montantTTC &&
//       !kinds.echeance &&
//       !kinds.jourPrelevement;

//     // Cas: modification uniquement du montant TTC
//     if (onlyAmount) {
//       const prev = metadata.previousMontantTTC as number | undefined;
//       const next = metadata.newMontantTTC as number | undefined;
//       if (typeof prev === "number" && typeof next === "number") {
//         return `${formatCurrency(prev)} -> ${formatCurrency(next)}`;
//       }
//       const match = d.match(/Montant TTC[^:]*:\s*([^€]+€)\s*→\s*([^€]+€)/);
//       if (match) {
//         return `${match[1].trim()} -> ${match[2].trim()}`;
//       }
//     }

//     // Cas: modification uniquement de la date d'échéance
//     if (onlyEcheance) {
//       const match = d.match(/Échéance[^:]*:\s*([^→]+)→\s*(.+)$/);
//       if (match) {
//         const prev = formatShortDateLabel(match[1]);
//         const next = formatShortDateLabel(match[2]);
//         return `${prev} -> ${next}`;
//       }
//     }

//     // Cas: modification uniquement du jour de prélèvement
//     if (onlyJour) {
//       const match = d.match(
//         /Jour de prélèvement récurrent modifié\s*:\s*([0-9]+)\s*→\s*([0-9]+)/
//       );
//       if (match) {
//         return `${match[1]} -> ${match[2]} jour du mois`;
//       }
//     }

//     // Cas: modification uniquement du taux/valeur de TVA
//     if (onlyTvaRate) {
//       const matchRate = d.match(/Taux TVA[^:]*:\s*([^%]+%)\s*→\s*([^%]+%)/);
//       if (matchRate) {
//         return `${matchRate[1].trim()} -> ${matchRate[2].trim()}`;
//       }
//       const matchAmount = d.match(/TVA[^:]*:\s*([^€]+€)\s*→\s*([^€]+€)/);
//       if (matchAmount) {
//         return `${matchAmount[1].trim()} -> ${matchAmount[2].trim()}`;
//       }
//     }
//   }

//   if (event.type === "echeance_update") {
//     const prev = metadata.previousEcheance as string | undefined;
//     const next = metadata.newEcheance as string | undefined;
//     if (prev || next) {
//       return `${formatShortDateLabel(prev || "(aucune)")} -> ${formatShortDateLabel(
//         next || ""
//       )}`;
//     }
//   }

//   if (event.type === "tva_override") {
//     // On privilégie le parsing de la chaîne de détails (toujours correcte),
//     // puis on retombe sur les métadonnées si nécessaire.
//     const match = d.match(/([^€]+€)\s*->\s*([^€]+€)/);
//     if (match) {
//       return `${match[1].trim()} -> ${match[2].trim()}`;
//     }

//     const prev = metadata.previousValue as number | undefined;
//     const next = metadata.newValue as number | undefined;
//     if (typeof prev === "number" && typeof next === "number") {
//       return `${formatCurrency(prev)} -> ${formatCurrency(next)}`;
//     }
//   }

//   if (event.type === "initial_balance_change") {
//     return d;
//   }

//   if (event.type === "create" || event.type === "delete") {
//     const amount = metadata.montantTTC as number | undefined;
//     if (typeof amount === "number") {
//       return formatCurrency(amount);
//     }
//   }

//   if (event.type === "archive") {
//     const prev = metadata.previousDateFin as string | undefined;
//     const next = metadata.newDateFin as string | undefined;
//     if (prev || next) {
//       return `${formatShortDateLabel(prev || "(aucune)")} -> ${formatShortDateLabel(
//         next || ""
//       )}`;
//     }
//   }

//   // Fallback: utiliser un résumé générique ou le détail brut
//   return getHistoryChangeSummary(event) || d;
// };

const formatCashFlowItemKind = (item: CashFlowItem): string => {
  const nature = item.isRevenue ? "Revenu" : "Dépense";
  const typeLabel =
    item.type === CashFlowItemType.RECURRENT ? "récurrent" : "ponctuel";

  const amount = `${item.montantTTC.toLocaleString("fr-FR")} €`;

  return `${nature} ${typeLabel} – ${item.categorie} – ${amount}`;
};

const buildUpdateHistoryDetails = (
  oldItem: CashFlowItem,
  newItem: CashFlowItem
): string => {
  const changes: string[] = [];
  let dayOfMonthChangeDescription: string | null = null;

  if (oldItem.libelle !== newItem.libelle) {
    changes.push(`Libellé : "${oldItem.libelle}" → "${newItem.libelle}"`);
  }

  if (oldItem.categorie !== newItem.categorie) {
    changes.push(`Catégorie : ${oldItem.categorie} → ${newItem.categorie}`);
  }

  if (oldItem.isRevenue !== newItem.isRevenue) {
    changes.push(
      `Sens : ${oldItem.isRevenue ? "revenu" : "dépense"} → ${
        newItem.isRevenue ? "revenu" : "dépense"
      }`
    );
  }

  if (oldItem.type !== newItem.type) {
    const typeLabel = (type: CashFlowItemType) =>
      type === CashFlowItemType.RECURRENT ? "récurrent" : "ponctuel";
    changes.push(
      `Type : ${typeLabel(oldItem.type)} → ${typeLabel(newItem.type)}`
    );
  }

  if (oldItem.montantTTC !== newItem.montantTTC) {
    changes.push(
      `Montant TTC : ${oldItem.montantTTC.toLocaleString(
        "fr-FR"
      )} € → ${newItem.montantTTC.toLocaleString("fr-FR")} €`
    );
  }

  if (oldItem.montantHT !== newItem.montantHT) {
    changes.push(
      `Montant HT : ${oldItem.montantHT.toLocaleString(
        "fr-FR"
      )} € → ${newItem.montantHT.toLocaleString("fr-FR")} €`
    );
  }

  if (oldItem.tva !== newItem.tva) {
    changes.push(
      `TVA : ${oldItem.tva.toLocaleString("fr-FR")} € → ${newItem.tva.toLocaleString(
        "fr-FR"
      )} €`
    );
  }

  if (oldItem.tauxTVA !== newItem.tauxTVA) {
    changes.push(`Taux TVA : ${oldItem.tauxTVA}% → ${newItem.tauxTVA}%`);
  }

  if (oldItem.echeance !== newItem.echeance) {
    const format = (d?: string) =>
      d ? dayjs(d).format("DD/MM/YYYY") : "(aucune)";
    changes.push(
      `Échéance : ${format(oldItem.echeance)} → ${format(newItem.echeance)}`
    );
  }

  if (oldItem.jourDuMois !== newItem.jourDuMois) {
    if (oldItem.jourDuMois || newItem.jourDuMois) {
      const fromLabel = oldItem.jourDuMois
        ? `${oldItem.jourDuMois}`
        : "aucun";
      const toLabel = newItem.jourDuMois
        ? `${newItem.jourDuMois}`
        : "aucun";
      const changeText = `Jour de prélèvement récurrent modifié : ${fromLabel} → ${toLabel}`;
      changes.push(changeText);
      dayOfMonthChangeDescription = changeText;
    }
  }

  if (changes.length === 0) {
    return formatCashFlowItemKind(newItem);
  }

  if (changes.length === 1 && dayOfMonthChangeDescription) {
    return `${dayOfMonthChangeDescription} pour ${formatCashFlowItemKind(
      newItem
    )}`;
  }

  return `${formatCashFlowItemKind(newItem)} • ${changes.join(" | ")}`;
};

// Fonction pour générer les données TVA depuis la base de données
const generateTVAData = (
  items: CashFlowItem[],
  selectedItems: Record<string, boolean>,
  overrides: TVAOverrides = {}
): {
  months: TVAMonthData[];
  operations: CashFlowItem[];
  prochaineMontantTVA?: {
    montant: number;
    libelle: string;
    date: string;
    isCredit: boolean;
  };
} => {
  const today = dayjs();
  const months: TVAMonthData[] = [];

  // Tableau pour stocker les opérations automatiques de TVA à générer
  const tvaOperationsToGenerate: CashFlowItem[] = [];

  // Filtrer les items actifs (sélectionnés)
  const activeItems = items.filter(
    (item) => !item._id || selectedItems[item._id] !== false
  );

  // Déterminer la plage des mois à analyser
  let oldestDate = today.clone();

  // Trouver la date la plus ancienne parmi tous les items
  activeItems.forEach((item) => {
    if (item.dateDebut) {
      const itemDate = dayjs(item.dateDebut);
      if (itemDate.isBefore(oldestDate)) {
        oldestDate = itemDate.clone();
      }
    }
    if (item.echeance) {
      const itemDate = dayjs(item.echeance);
      if (itemDate.isBefore(oldestDate)) {
        oldestDate = itemDate.clone();
      }
    }
  });

  // Calculer le nombre de mois entre aujourd'hui et la date la plus ancienne
  // En utilisant le premier jour du mois pour oldestDate pour s'assurer que le mois entier est inclus
  const oldestDateStartOfMonth = oldestDate.startOf("month");
  const todayStartOfMonth = today.startOf("month");

  // On calcule la différence en mois entre aujourd'hui et la date la plus ancienne
  // +1 pour s'assurer d'inclure le mois entier même si la date est en fin de mois
  const diffMonths = todayStartOfMonth.diff(oldestDateStartOfMonth, "month");

  // Générer les données TVA pour chaque mois, du plus récent au plus ancien
  for (let i = 0; i <= diffMonths; i++) {
    const currentMonth = today.clone().subtract(i, "month");
    const monthStart = currentMonth.startOf("month");
    const monthEnd = currentMonth.endOf("month");
    const monthName = currentMonth.format("MMMM YYYY");
    const monthKey = currentMonth.startOf("month").format("YYYY-MM-DD");

    const monthData: TVAMonthData = {
      mois: monthName,
      monthKey,
      revenus: [],
      depenses: [],
      soldeTVA: 0,
    };

    // Traiter les items ponctuels
    activeItems.forEach((item) => {
      if (item.type === CashFlowItemType.PONCTUEL && item.echeance) {
        const date = dayjs(item.echeance);

        // Format les dates en chaînes de caractères YYYY-MM-DD pour comparer uniquement les dates sans les heures
        const dateStr = date.format("YYYY-MM-DD");
        const monthStartStr = monthStart.format("YYYY-MM-DD");
        const monthEndStr = monthEnd.format("YYYY-MM-DD");

        // Vérifier si l'échéance est dans ce mois (comparison des dates sans les heures)
        if (dateStr >= monthStartStr && dateStr <= monthEndStr) {
          const tvaItem: TVAItem = {
            id: `${item._id || "unknown"}-${date.format("YYYY-MM-DD")}`,
            libelle: item.libelle,
            montantTTC: item.montantTTC,
            montantHT: item.montantHT || 0,
            tauxTVA: item.tauxTVA || 0,
            tva: item.tva || item.montantTTC - (item.montantHT || 0),
            isRevenue: item.categorie?.includes("revenu") || false,
            date: date.format("DD/MM/YYYY"),
            mois: monthName,
          };

          if (tvaItem.isRevenue) {
            monthData.revenus.push(tvaItem);
          } else {
            monthData.depenses.push(tvaItem);
          }
        }
      } else if (item.type === CashFlowItemType.RECURRENT && item.jourDuMois) {
        // Traiter les items récurrents
        const jourDuMois = item.jourDuMois;
        let dateOccurence = monthStart.date(
          jourDuMois > monthEnd.date() ? monthEnd.date() : jourDuMois
        );

        // Vérifier si cette occurrence est valide pour ce mois en tenant compte de la date de début et de fin
        const dateDebut = item.dateDebut ? dayjs(item.dateDebut) : null;
        const dateFin = item.dateFin ? dayjs(item.dateFin) : null;

        // Pour une opération récurrente, si la date de début est après le jour du mois dans le mois courant,
        // la première occurrence doit être dans le mois suivant
        let premierMoisValide = true;
        if (dateDebut) {
          const debutMois = dateDebut.month();
          const debutAnnee = dateDebut.year();
          const debutJour = dateDebut.date();

          // Si le mois courant est le même que le mois de début
          if (
            debutMois === currentMonth.month() &&
            debutAnnee === currentMonth.year()
          ) {
            // Vérifier si la date de début est après le jour du mois pour ce même mois
            premierMoisValide = debutJour <= jourDuMois;
          }
        }

        // L'opération est valide pour ce mois si:
        // 1. La date de début est avant la fin du mois (ou identique)
        const debutValide =
          !dateDebut ||
          dateDebut.isBefore(monthEnd) ||
          dateDebut.isSame(monthEnd, "day");

        // 2. ET la date de fin n'est pas définie OU est après le début du mois (ou identique)
        const finValide =
          !dateFin ||
          dateFin.isAfter(monthStart) ||
          dateFin.isSame(monthStart, "day");

        // 3. Pour le premier mois, vérifier la logique spéciale
        const isValidForMonth = debutValide && finValide && premierMoisValide;

        if (isValidForMonth) {
          const tvaItem: TVAItem = {
            id: `${item._id || "unknown"}-${dateOccurence.format(
              "YYYY-MM-DD"
            )}`,
            libelle: item.libelle,
            montantTTC: item.montantTTC,
            montantHT: item.montantHT || 0,
            tauxTVA: item.tauxTVA || 0,
            tva: item.tva || item.montantTTC - (item.montantHT || 0),
            isRevenue: item.categorie?.includes("revenu") || false,
            date: dateOccurence.format("DD/MM/YYYY"),
            mois: monthName,
          };

          if (tvaItem.isRevenue) {
            monthData.revenus.push(tvaItem);
          } else {
            monthData.depenses.push(tvaItem);
          }
        }
      }
    });

    // Calculer le solde TVA pour le mois (TVA due sur revenus - TVA déductible sur dépenses)
    const tvaDueRevenus = monthData.revenus.reduce(
      (sum, item) => sum + item.tva,
      0
    );
    const tvaDeductibleDepenses = monthData.depenses.reduce(
      (sum, item) => sum + item.tva,
      0
    );
    monthData.soldeTVA = tvaDueRevenus - tvaDeductibleDepenses;

    const baseSolde = monthData.soldeTVA;
    const override = overrides[monthName];
    const effectiveSolde = override !== undefined ? override : baseSolde;
    if (override !== undefined) {
      monthData.overrideSoldeTVA = override;
    }

    // Ajouter ce mois à la liste des mois (s'il contient des données)
    if (monthData.revenus.length > 0 || monthData.depenses.length > 0) {
      months.push(monthData);
    }

    // Générer les opérations de TVA automatiques
    // 1. Si solde TVA > 0 (TVA à payer), créer prélèvement au 27 du mois suivant (M+1)
    if (effectiveSolde > 0) {
      const nextMonth = currentMonth.add(1, "month");
      const datePaiement = nextMonth.date(27);
      const echeanceStr = datePaiement.format("YYYY-MM-DD");

      tvaOperationsToGenerate.push({
        _id: `tva-paiement-${monthData.mois.replace(/ /g, "-")}`,
        libelle: `Paiement TVA ${monthData.mois}`,
        montantTTC: effectiveSolde,
        montantHT: effectiveSolde, // Pas de TVA sur le paiement de la TVA
        tva: 0,
        tauxTVA: 0,
        categorie: "depense_exceptionnelle",
        category: "depense_exceptionnelle",
        type: CashFlowItemType.PONCTUEL,
        echeance: echeanceStr,
        dateDebut: today.format("YYYY-MM-DD"),
        isRevenue: false,
      });
    }
    // 2. Si solde TVA < 0 (crédit de TVA), créer entrée de cash au 5 du mois M+2
    else if (effectiveSolde < 0) {
      const monthPlusTwo = currentMonth.add(2, "month");
      const dateCredit = monthPlusTwo.date(5);
      const echeanceStr = dateCredit.format("YYYY-MM-DD");

      tvaOperationsToGenerate.push({
        _id: `tva-credit-${monthData.mois.replace(/ /g, "-")}`,
        libelle: `Crédit de TVA ${monthData.mois}`,
        montantTTC: Math.abs(effectiveSolde),
        montantHT: Math.abs(effectiveSolde), // Pas de TVA sur le remboursement de TVA
        tva: 0,
        tauxTVA: 0,
        categorie: "revenu_exceptionnel",
        category: "revenu_exceptionnel",
        type: CashFlowItemType.PONCTUEL,
        echeance: echeanceStr,
        dateDebut: today.format("YYYY-MM-DD"),
        isRevenue: true,
      });
    }
  }

  // Déterminer la prochaine opération TVA (la plus proche dans le temps)
  let prochaineMontantTVA;
  if (tvaOperationsToGenerate.length > 0) {
    // Trier les opérations par date d'échéance
    const operationsTriees = [...tvaOperationsToGenerate].sort((a, b) => {
      const dateA = dayjs(a.echeance);
      const dateB = dayjs(b.echeance);
      return dateA.diff(dateB);
    });

    // Trouver la première opération future
    const prochaineOperation = operationsTriees.find(
      (op) =>
        (op.echeance && dayjs(op.echeance).isAfter(today)) ||
        dayjs(op.echeance).isSame(today, "day")
    );

    if (prochaineOperation) {
      prochaineMontantTVA = {
        montant: prochaineOperation.montantTTC,
        libelle: prochaineOperation.libelle,
        date: dayjs(prochaineOperation.echeance).format("DD/MM/YYYY"),
        isCredit: prochaineOperation.isRevenue,
      };
    }
  }
  // Générer des mois futurs si nécessaire (anticiper mois+1 et mois+2)
  const currentDate = today.clone();
  // Fix: Créer de nouvelles instances pour chaque mois futur
  const nextMonth = today.clone().add(1, "month");
  const nextNextMonth = today.clone().add(2, "month");

  // Vérifier si les mois futurs sont déjà inclus dans les données
  const monthNames = months.map((m) => m.mois);
  const nextMonthName = nextMonth.format("MMMM YYYY");
  const nextNextMonthName = nextNextMonth.format("MMMM YYYY");

  // Ajouter des mois vides pour les mois futurs s'ils n'existent pas déjà
  let nextMonthData: TVAMonthData | undefined;
  if (!monthNames.includes(nextMonthName)) {
    nextMonthData = {
      mois: nextMonthName,
      monthKey: nextMonth.startOf("month").format("YYYY-MM-DD"),
      revenus: [],
      depenses: [],
      soldeTVA: 0,
      totalRevenus: 0,
      totalDepenses: 0,
      totalTVA: 0,
    };
    months.push(nextMonthData);
  } else {
    nextMonthData = months.find((m) => m.mois === nextMonthName);
  }

  let nextNextMonthData: TVAMonthData | undefined;
  if (!monthNames.includes(nextNextMonthName)) {
    nextNextMonthData = {
      mois: nextNextMonthName,
      monthKey: nextNextMonth.startOf("month").format("YYYY-MM-DD"),
      revenus: [],
      depenses: [],
      soldeTVA: 0,
      totalRevenus: 0,
      totalDepenses: 0,
      totalTVA: 0,
    };
    months.push(nextNextMonthData);
  } else {
    nextNextMonthData = months.find((m) => m.mois === nextNextMonthName);
  }

  // Traiter les opérations ponctuelles pour les mois futurs
  activeItems.forEach((item: CashFlowItem) => {
    if (item.type === CashFlowItemType.PONCTUEL && item.echeance) {
      const echeanceDate = dayjs(item.echeance);

      // Vérifier si l'échéance est dans le mois suivant (M+1)
      if (
        echeanceDate.month() === nextMonth.month() &&
        echeanceDate.year() === nextMonth.year() &&
        nextMonthData
      ) {
        addItemToTVAMonth(item, echeanceDate, nextMonthData, nextMonthName);
      }

      // Vérifier si l'échéance est dans le mois d'après (M+2)
      if (
        echeanceDate.month() === nextNextMonth.month() &&
        echeanceDate.year() === nextNextMonth.year() &&
        nextNextMonthData
      ) {
        addItemToTVAMonth(
          item,
          echeanceDate,
          nextNextMonthData,
          nextNextMonthName
        );
      }
    }
  });

  // Fonction auxiliaire pour ajouter un item à un mois TVA
  function addItemToTVAMonth(
    item: CashFlowItem,
    date: dayjs.Dayjs,
    monthData: TVAMonthData,
    monthName: string
  ) {
    const tvaItem: TVAItem = {
      id: `${item._id || "unknown"}-${date.format("YYYY-MM-DD")}`,
      libelle: item.libelle,
      montantTTC: item.montantTTC,
      montantHT: item.montantHT || 0,
      tauxTVA: item.tauxTVA || 0,
      tva: item.tva || item.montantTTC - (item.montantHT || 0),
      isRevenue: item.categorie?.includes("revenu") || false,
      date: date.format("DD/MM/YYYY"),
      mois: monthName,
    };

    if (tvaItem.isRevenue) {
      monthData.revenus.push(tvaItem);
    } else {
      monthData.depenses.push(tvaItem);
    }

    // Ne pas générer d'opérations TVA individuelles
    // Seul le solde TVA global doit être affiché dans le graphique
  }

  // Traiter spécialement les opérations récurrentes pour les mois futurs
  activeItems.forEach((item: CashFlowItem) => {
    if (item.type === CashFlowItemType.RECURRENT && item.jourDuMois) {
      // Traitement pour le mois suivant (mois+1)
      processRecurringItemForMonth(
        item,
        nextMonth,
        nextMonthName,
        nextMonthData
      );

      // Traitement pour le mois d'après (mois+2)
      processRecurringItemForMonth(
        item,
        nextNextMonth,
        nextNextMonthName,
        nextNextMonthData
      );
    }
  });

  // Fonction auxiliaire pour traiter une opération récurrente pour un mois donné
  function processRecurringItemForMonth(
    item: CashFlowItem,
    monthDate: dayjs.Dayjs,
    monthName: string,
    monthData?: TVAMonthData
  ) {
    if (!monthData) return;

    const jourDuMois = item.jourDuMois || 1; // Défaut au 1er du mois si non défini
    const monthStart = monthDate.startOf("month");
    const monthEnd = monthDate.endOf("month");

    let dateOccurence = monthStart.date(
      jourDuMois > monthEnd.date() ? monthEnd.date() : jourDuMois
    );

    // Vérifier si cette occurrence est valide pour ce mois en tenant compte de la date de début et de fin
    const dateDebut = item.dateDebut ? dayjs(item.dateDebut) : null;
    const dateFin = item.dateFin ? dayjs(item.dateFin) : null;

    // L'opération est valide pour ce mois si:
    // 1. La date de début est avant la fin du mois (ou identique)
    const debutValide =
      !dateDebut ||
      dateDebut.isBefore(monthEnd) ||
      dateDebut.isSame(monthEnd, "day");

    // 2. ET la date de fin n'est pas définie OU est après le début du mois (ou identique)
    const finValide =
      !dateFin ||
      dateFin.isAfter(monthStart) ||
      dateFin.isSame(monthStart, "day");

    // Pour un mois futur, aucune logique spéciale n'est nécessaire pour le premier mois
    const isValidForMonth = debutValide && finValide;

    if (isValidForMonth) {
      // Créer un TVAItem pour l'opération récurrente
      const tvaItem: TVAItem = {
        id: `${item._id || "unknown"}-${dateOccurence.format("YYYY-MM-DD")}`,
        libelle: item.libelle,
        montantTTC: item.montantTTC,
        montantHT: item.montantHT || 0,
        tauxTVA: item.tauxTVA || 0,
        tva: item.tva || item.montantTTC - (item.montantHT || 0),
        isRevenue: item.categorie?.includes("revenu") || false,
        date: dateOccurence.format("DD/MM/YYYY"),
        mois: monthName,
      };

      if (tvaItem.isRevenue) {
        monthData.revenus.push(tvaItem);
      } else {
        monthData.depenses.push(tvaItem);
      }

      // Générer une opération TVA pour ce mois futur
      if (
        (tvaItem.isRevenue && tvaItem.tauxTVA > 0) ||
        (!tvaItem.isRevenue && tvaItem.tva > 0)
      ) {
        tvaOperationsToGenerate.push({
          _id: `tva-${tvaItem.id}`,
          libelle: `TVA ${tvaItem.isRevenue ? "collectée" : "à payer"} - ${
            tvaItem.libelle
          }`,
          montantTTC: tvaItem.tva,
          echeance: dateOccurence.format("YYYY-MM-DD"),
          categorie: tvaItem.isRevenue ? "depense_fiscale" : "revenu_tva",
          type: CashFlowItemType.PONCTUEL,
        } as CashFlowItem);
      }
    }
  }

  // Calculer le solde TVA, totalRevenus et totalDepenses pour tous les mois, y compris les mois futurs
  months.forEach((month) => {
    // Calculer les totaux pour ce mois
    let totalRevenusMonth = 0;
    let totalDepensesMonth = 0;
    let totalTVAMonth = 0;

    month.revenus.forEach((item) => {
      totalRevenusMonth += item.montantTTC || 0;
      totalTVAMonth += item.tva || 0;
    });

    month.depenses.forEach((item) => {
      totalDepensesMonth += item.montantTTC || 0;
      totalTVAMonth -= item.tva || 0; // TVA déductible est soustraite
    });

    // Mise à jour des totaux et solde
    month.totalRevenus = totalRevenusMonth;
    month.totalDepenses = totalDepensesMonth;
    month.totalTVA = totalTVAMonth;
    month.soldeTVA = totalTVAMonth;
  });

  // Nouveau tri des mois avec une logique spéciale:
  // 1. Mettre d'abord mois courant + 2
  // 2. Puis mois courant + 1
  // 3. Puis mois courant
  // 4. Puis les mois du plus récent au plus ancien
  const currentMonthName = currentDate.format("MMMM YYYY");

  const sortedMonths = months.sort((a, b) => {
    const aDate = dayjs(a.mois, "MMMM YYYY");
    const bDate = dayjs(b.mois, "MMMM YYYY");

    // Cas spéciaux pour les 3 premiers mois
    if (a.mois === nextNextMonthName) return -1;
    if (b.mois === nextNextMonthName) return 1;
    if (a.mois === nextMonthName) return -1;
    if (b.mois === nextMonthName) return 1;
    if (a.mois === currentMonthName) return -1;
    if (b.mois === currentMonthName) return 1;

    // Pour les autres mois, tri standard du plus récent au plus ancien
    return bDate.diff(aDate);
  });

  // Retourner les mois, les opérations générées, et la prochaine opération TVA
  return {
    months: sortedMonths, // Tri du plus récent au plus ancien
    operations: tvaOperationsToGenerate,
    prochaineMontantTVA,
  };
};

// Fonction pour calculer le solde cumulatif à partir d'un solde initial
const calculateCumulativeBalance = (
  items: CashFlowItem[],
  initialBalance: number,
  dateRange: [dayjs.Dayjs, dayjs.Dayjs] // Période sélectionnée
): BalanceDataPoint[] => {
  let balance = initialBalance;
  let consolidatedData: Record<
    string,
    { balance: number; transactions: number }
  > = {};
  const startDate = dateRange[0].startOf("day");
  const endDate = dateRange[1].endOf("day");
  const allItems: {
    date: string;
    montant: number;
    isRevenue: boolean;
    isTVASolde?: boolean;
  }[] = [];

  // Point initial avec la date de début et le solde initial
  consolidatedData[startDate.format("YYYY-MM-DD")] = {
    balance: initialBalance,
    transactions: 0,
  };

  // Générer les items à prendre en compte dans la période
  items.forEach((item) => {
    if (!item) return;

    // Valeurs par défaut
    const montant = item.montantTTC || 0;
    const isRevenue = item.categorie?.includes("revenu") || false;

    if (item.type === CashFlowItemType.RECURRENT) {
      // Pour les items récurrents, générer toutes les occurrences dans la période
      const jourDuMois = item.jourDuMois || 1; // Jour par défaut: 1er du mois
      const dateDebut = item.dateDebut ? dayjs(item.dateDebut) : startDate;

      // Ne pas prendre en compte les échéances antérieures à dateDebut
      let currentMonth = dateDebut.isAfter(startDate) ? dateDebut : startDate;

      // Générer les occurrences mensuelles jusqu'à la fin de la période
      while (
        currentMonth.isBefore(endDate) ||
        currentMonth.isSame(endDate, "month")
      ) {
        // Créer la date d'échéance pour ce mois
        let dateEcheance = currentMonth.date(jourDuMois);

        // Vérifier si le jour existe dans ce mois (ex: 31 février)
        if (dateEcheance.month() !== currentMonth.month()) {
          dateEcheance = currentMonth.endOf("month"); // Prendre le dernier jour du mois
        }

        // Ajouter cette échéance si elle est dans la période sélectionnée
        if (
          (dateEcheance.isAfter(startDate) ||
            dateEcheance.isSame(startDate, "day")) &&
          (dateEcheance.isBefore(endDate) ||
            dateEcheance.isSame(endDate, "day"))
        ) {
          allItems.push({
            date: dateEcheance.format("YYYY-MM-DD"),
            montant: montant,
            isRevenue: isRevenue,
          });
        }

        // Passer au mois suivant
        currentMonth = currentMonth.add(1, "month");
      }
    } else if (item.type === CashFlowItemType.PONCTUEL) {
      let echeanceDate;

      if (item.echeance) {
        echeanceDate = dayjs(item.echeance);
      } else if (item.dateDebut) {
        echeanceDate = dayjs(item.dateDebut);
      } else {
        return; // Si pas de date d'échéance ou de début, on ignore l'élément
      }

      // Vérifier si l'échéance est dans la période
      if (
        (echeanceDate.isAfter(startDate) ||
          echeanceDate.isSame(startDate, "day")) &&
        (echeanceDate.isBefore(endDate) || echeanceDate.isSame(endDate, "day"))
      ) {
        allItems.push({
          date: echeanceDate.format("YYYY-MM-DD"),
          montant: montant,
          isRevenue: isRevenue,
        });
      }
    }
  });

  // Trier tous les items par date
  allItems.sort((a, b) => {
    return dayjs(a.date).diff(dayjs(b.date));
  });

  // Regrouper par date et calculer le solde cumulatif
  for (const item of allItems) {
    balance += item.isRevenue ? item.montant : -item.montant;

    if (consolidatedData[item.date]) {
      // Si la date existe déjà, mise à jour du solde
      consolidatedData[item.date].balance = balance;
      consolidatedData[item.date].transactions++;
    } else {
      // Sinon création d'un nouveau point
      consolidatedData[item.date] = { balance, transactions: 1 };
    }
  }

  // Conversion du dictionnaire en tableau trié par date
  const balanceData: BalanceDataPoint[] = Object.entries(consolidatedData)
    .map(([date, data]) => ({
      date,
      balance: data.balance,
    }))
    .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));

  return balanceData;
};


// Type pour la modale de catégorie
interface CategoryModalData {
  category: string;
  items: CashFlowItem[];
  isRevenue: boolean;
}

// Type pour le formulaire de création de dépense/revenu
interface CreateItemFormData {
  category: string;
  isRevenue: boolean;
  libelle: string;
  montantHT: number;
  montantTTC: number;
  tva: number;
  tauxTVA: 0 | 5.5 | 10 | 20;
  type: CashFlowItemType;
  echeance: string;
  jourDuMois?: number; // Jour du mois pour les prélèvements récurrents
  inputMode: "HT" | "TTC"; // Mode de saisie (HT ou TTC)
}

// Fonction pour déterminer la catégorie à partir des données de l'API
const determinerCategorie = (
  category: string | undefined,
  isRevenue: boolean | undefined
): string => {
  const estRevenu = isRevenue === true;

  if (!category)
    return estRevenu ? "revenu_exceptionnel" : "depense_exceptionnelle";

  if (category && category.includes("recurrent"))
    return estRevenu ? "revenu_vente" : "depense_recurrente";
  if (category && category.includes("vente"))
    return estRevenu ? "revenu_vente" : "depense_vente";

  return estRevenu ? "revenu_exceptionnel" : "depense_exceptionnelle";
};

const Finance: React.FC = () => {
  const dispatch = useAppDispatch();
  const cashFlowState = useAppSelector((state) => state.cashFlow);
  const initialBalance = cashFlowState.initialBalance;
  const [historyEvents, setHistoryEvents] =
    useState<CashFlowHistoryEvent[]>([]);
  const [historyCurrentSlide, setHistoryCurrentSlide] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const pushHistoryEvent = (
    payload: Omit<CashFlowHistoryEvent, "id" | "timestamp">,
    options?: { cashFlowId?: string; metadata?: any }
  ) => {
    const event: CashFlowHistoryEvent = {
      ...payload,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: dayjs().toISOString(),
      metadata: options?.metadata,
    };

    setHistoryEvents((prev) => {
      const cutoff = dayjs().subtract(HISTORY_WINDOW_DAYS, "day");
      const kept = prev.filter((ev) => dayjs(ev.timestamp).isAfter(cutoff));
      return [event, ...kept].slice(0, 50);
    });

    // Enregistrement en base de données (fire-and-forget)
    void logCashFlowHistoryEvent({
      cashFlowId: options?.cashFlowId,
      type: payload.type,
      title: payload.title,
      details: payload.details,
      metadata: options?.metadata,
    }).catch((error) => {
      console.error(
        "Erreur lors de l'enregistrement de l'historique de cash flow:",
        error
      );
    });
  };

  // États pour les modérateurs
  const [cashFlowItems, setCashFlowItems] = useState<CashFlowItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>(
    {}
  );
  const [tvaData, setTVAData] = useState<TVAMonthData[]>([]);
  const [tvaModalVisible, setTVAModalVisible] = useState(false);
  const [tvaOverrides, setTVAOverrides] = useState<TVAOverrides>(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem("cashflow_tva_overrides");
      return raw ? (JSON.parse(raw) as TVAOverrides) : {};
    } catch {
      return {};
    }
  });
  const tvaOverrideLastValuesRef = useRef<
    Record<string, { previous: number; next: number }>
  >({});
  const tvaOverrideTimersRef = useRef<Record<string, number | undefined>>({});
  const [tvaResult, setTVAResult] = useState<
    | {
        months: TVAMonthData[];
        operations: CashFlowItem[];
        prochaineMontantTVA?: {
          montant: number;
          libelle: string;
          date: string;
        } | null;
      }
    | null
  >(null);
  const initialBalanceLastValuesRef = useRef<{
    previous: number;
    next: number;
  } | null>(null);
  const initialBalanceTimerRef = useRef<number | undefined>(undefined);

  // États pour l'édition et la suppression
  const [editingItem, setEditingItem] = useState<CashFlowItem | null>(null);

  // État pour le tooltip d'archivage (date de fin)
  const [archiveTooltipVisible, setArchiveTooltipVisible] = useState(false);
  const [archivingItem, setArchivingItem] = useState<CashFlowItem | null>(null);
  const [archiveDate, setArchiveDate] = useState<dayjs.Dayjs | null>(
    dayjs().add(1, "month")
  );

  // --- Synchronisation (Sync Rules) ---
  const [syncViewMode, setSyncViewMode] = useState<"list" | "form">("list");
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncPreviewLoading, setSyncPreviewLoading] = useState(false);
  const [syncPreview, setSyncPreview] = useState<APICashFlowSyncProposal[]>([]);

  const [syncMetadataLoading, setSyncMetadataLoading] = useState(false);
  const [syncMetadataModels, setSyncMetadataModels] = useState<APICashFlowSyncMetadataModel[]>([]);
  const [syncMetadataSelectedModel, setSyncMetadataSelectedModel] = useState<string | null>(null);
  const [syncMetadataSelectedAmountPath, setSyncMetadataSelectedAmountPath] = useState<string | null>(null);
  const [syncConfigAmountFallbackPaths, setSyncConfigAmountFallbackPaths] = useState<string[]>([]);

  const [syncRulesLoading, setSyncRulesLoading] = useState(false);
  const [syncRules, setSyncRules] = useState<APICashFlowSyncRule[]>([]);
  const [syncSelectedRuleId, setSyncSelectedRuleId] = useState<string | null>(null);
  const [syncRuleName, setSyncRuleName] = useState<string>("");
  const [syncRuleEnabled, setSyncRuleEnabled] = useState<boolean>(true);
  const [syncSaveLoading, setSyncSaveLoading] = useState(false);

  const [syncConfigCategory, setSyncConfigCategory] = useState<string>("depense_vente");
  const [syncConfigIsRevenue, setSyncConfigIsRevenue] = useState<boolean>(false);
  const [syncConfigTitleTemplate, setSyncConfigTitleTemplate] = useState<string>("{_id}");
  const [syncConfigDueDateBasePath, setSyncConfigDueDateBasePath] = useState<string>("createdAt");
  const [syncConfigDueDateBasePaths, setSyncConfigDueDateBasePaths] = useState<string[]>(["createdAt"]);
  const [syncConfigDueDateTransform, setSyncConfigDueDateTransform] = useState<"none" | "end_of_month">("none");
  const [syncConfigDueDateOffsetDays, setSyncConfigDueDateOffsetDays] = useState<number>(0);
  const [syncConfigTauxTVA, setSyncConfigTauxTVA] = useState<0 | 5.5 | 10 | 20>(20);

  const [syncConfigTvaMode, setSyncConfigTvaMode] = useState<
    "none" | "fixed_rate" | "rate_from_field"
  >("fixed_rate");
  const [syncConfigTvaRatePath, setSyncConfigTvaRatePath] = useState<string>("");

  const [syncConfigOffsetMode, setSyncConfigOffsetMode] = useState<
    "constant_days" | "field_days"
  >("constant_days");
  const [syncConfigOffsetDaysPath, setSyncConfigOffsetDaysPath] = useState<string>("");

  const openSyncModal = async () => {
    setSyncModalVisible(true);
    setSyncViewMode("list");
    setSyncPreview([]);

    try {
      setSyncRulesLoading(true);
      const rules = await getCashFlowSyncRules();
      setSyncRules(Array.isArray(rules) ? rules : []);

      // Charger aussi les metadata de modèles pour test frontend (sources 100% dynamiques)
      setSyncMetadataLoading(true);
      const meta = await getCashFlowSyncMetadata();
      const models = Array.isArray(meta?.models) ? meta.models : [];
      setSyncMetadataModels(models);

      if (!syncMetadataSelectedModel && models.length > 0) {
        setSyncMetadataSelectedModel(models[0].modelName);
      }
    } catch (e: any) {
      message.error(`Erreur chargement synchronisations: ${e?.message || e}`);
    } finally {
      setSyncMetadataLoading(false);
      setSyncRulesLoading(false);
    }
  };

  const selectedMetadataModel = useMemo(() => {
    if (!syncMetadataSelectedModel) return null;
    return (
      syncMetadataModels.find((m) => m.modelName === syncMetadataSelectedModel) || null
    );
  }, [syncMetadataModels, syncMetadataSelectedModel]);

  const loadRuleIntoConfigurator = (rule: APICashFlowSyncRule) => {
    setSyncSelectedRuleId(rule._id ? String(rule._id) : null);
    setSyncRuleName(String(rule.name || ""));
    setSyncRuleEnabled(rule.enabled !== false);

    const isV2 = Number(rule.version || 1) >= 2 || !!rule.v2;

    setSyncMetadataSelectedModel(String(rule.sourceModel || ""));
    setSyncMetadataSelectedAmountPath(
      String((isV2 ? rule.v2?.amount?.path : rule.amountPath) || "")
    );

    if (isV2) {
      const amountFallbacks = Array.isArray((rule.v2?.amount as any)?.fallbackPaths)
        ? (((rule.v2?.amount as any)?.fallbackPaths as any) || [])
        : Array.isArray((rule as any)?.amountFallbackPaths)
          ? (((rule as any)?.amountFallbackPaths as any) || [])
          : [];
      setSyncConfigAmountFallbackPaths(amountFallbacks.map((p: any) => String(p)));
    } else {
      const amountFallbacks = Array.isArray((rule as any)?.amountFallbackPaths)
        ? (((rule as any)?.amountFallbackPaths as any) || [])
        : [];
      setSyncConfigAmountFallbackPaths(amountFallbacks.map((p: any) => String(p)));
    }

    setSyncConfigCategory(String(rule.category || "depense_vente"));
    setSyncConfigIsRevenue(!!rule.isRevenue);
    setSyncConfigTitleTemplate(String(rule.titleTemplate || "{_id}"));

    if (isV2) {
      const basePaths = Array.isArray((rule.v2?.dueDate as any)?.baseDatePaths)
        ? (((rule.v2?.dueDate as any)?.baseDatePaths as any) || [])
        : [];
      const first = String(rule.v2?.dueDate?.baseDatePath || rule.dueDateBasePath || "createdAt");
      setSyncConfigDueDateBasePath(first);
      setSyncConfigDueDateBasePaths(basePaths.length ? basePaths.map((p: any) => String(p)) : [first]);
      setSyncConfigDueDateTransform(
        (String(rule.v2?.dueDate?.transform || "none") as any) || "none"
      );
      const offsets = Array.isArray(rule.v2?.dueDate?.offsets) ? rule.v2?.dueDate?.offsets : [];
      const fieldOffset = offsets.find((o) => o.kind === "field_days");
      const constant = offsets.find((o) => o.kind === "constant_days");
      if (fieldOffset && fieldOffset.path) {
        setSyncConfigOffsetMode("field_days");
        setSyncConfigOffsetDaysPath(String(fieldOffset.path));
      } else {
        setSyncConfigOffsetMode("constant_days");
        setSyncConfigOffsetDaysPath("");
      }
      setSyncConfigDueDateOffsetDays(Number(constant?.days || 0));

      const tvaMode = String(rule.v2?.tva?.mode || "none");
      if (tvaMode === "rate_from_field") {
        setSyncConfigTvaMode("rate_from_field");
        setSyncConfigTvaRatePath(String(rule.v2?.tva?.ratePath || ""));
        setSyncConfigTauxTVA((Number(rule.tauxTVA || 0) as any) || 0);
      } else if (tvaMode === "fixed_rate") {
        setSyncConfigTvaMode("fixed_rate");
        setSyncConfigTvaRatePath("");
        setSyncConfigTauxTVA((Number(rule.v2?.tva?.rate || 0) as any) || 0);
      } else {
        setSyncConfigTvaMode("none");
        setSyncConfigTvaRatePath("");
        setSyncConfigTauxTVA(0);
      }
    } else {
      setSyncConfigDueDateBasePath(String(rule.dueDateBasePath || "createdAt"));
      setSyncConfigDueDateBasePaths([String(rule.dueDateBasePath || "createdAt")]);
      setSyncConfigDueDateTransform("none");
      setSyncConfigDueDateOffsetDays(Number(rule.dueDateOffsetDays || 0));
      setSyncConfigTauxTVA((Number(rule.tauxTVA || 0) as any) || 0);
      setSyncConfigOffsetMode("constant_days");
      setSyncConfigOffsetDaysPath("");
      setSyncConfigTvaMode(rule.tauxTVA ? "fixed_rate" : "none");
      setSyncConfigTvaRatePath("");
    }
  };

  const startCreateSyncRule = async () => {
    setSyncSelectedRuleId(null);
    setSyncRuleName("");
    setSyncRuleEnabled(true);
    setSyncPreview([]);

    // Defaults côté form
    setSyncConfigCategory("depense_vente");
    setSyncConfigIsRevenue(false);
    setSyncConfigTitleTemplate("{_id}");
    setSyncConfigDueDateBasePath("createdAt");
    setSyncConfigDueDateBasePaths(["createdAt"]);
    setSyncConfigAmountFallbackPaths([]);
    setSyncConfigDueDateTransform("none");
    setSyncConfigDueDateOffsetDays(0);
    setSyncConfigTauxTVA(20);

    setSyncConfigTvaMode("fixed_rate");
    setSyncConfigTvaRatePath("");
    setSyncConfigOffsetMode("constant_days");
    setSyncConfigOffsetDaysPath("");

    setSyncViewMode("form");
  };

  const startEditSyncRule = (rule: APICashFlowSyncRule) => {
    loadRuleIntoConfigurator(rule);
    setSyncPreview([]);
    setSyncViewMode("form");
  };

  const deleteSelectedSyncRule = async (id: string) => {
    try {
      await deleteCashFlowSyncRule(id);
      message.success("Synchronisation supprimée");
      const rules = await getCashFlowSyncRules();
      setSyncRules(Array.isArray(rules) ? rules : []);
      if (syncSelectedRuleId === id) {
        setSyncSelectedRuleId(null);
      }
    } catch (e: any) {
      message.error(`Erreur suppression: ${e?.message || e}`);
    }
  };

  const saveRuleFromConfigurator = async () => {
    if (!syncRuleName.trim()) {
      message.error("Nom de règle requis");
      return;
    }
    if (!syncMetadataSelectedModel) {
      message.error("Modèle source requis");
      return;
    }
    if (!syncMetadataSelectedAmountPath) {
      message.error("Champs montant requis");
      return;
    }
    if (!syncConfigTitleTemplate.trim()) {
      message.error("Template libellé requis");
      return;
    }

    const payload: Omit<APICashFlowSyncRule, "_id" | "createdAt" | "updatedAt"> = {
      version: 2,
      name: syncRuleName.trim(),
      enabled: syncRuleEnabled,
      sourceModel: syncMetadataSelectedModel,
      category: syncConfigCategory,
      isRevenue: syncConfigIsRevenue,
      titleTemplate: syncConfigTitleTemplate,
      v2: {
        amount: {
          path: syncMetadataSelectedAmountPath,
          fallbackPaths: syncConfigAmountFallbackPaths,
        },
        dueDate: {
          baseDatePath: syncConfigDueDateBasePath,
          baseDatePaths: syncConfigDueDateBasePaths,
          transform: syncConfigDueDateTransform,
          offsets:
            syncConfigOffsetMode === "field_days" && syncConfigOffsetDaysPath
              ? [
                  {
                    kind: "field_days",
                    path: syncConfigOffsetDaysPath,
                  },
                ]
              : [
                  {
                    kind: "constant_days",
                    days: syncConfigDueDateOffsetDays,
                  },
                ],
        } as any,
        tva: {
          mode: syncConfigTvaMode,
          rate: syncConfigTvaMode === "fixed_rate" ? syncConfigTauxTVA : 0,
          ratePath: syncConfigTvaMode === "rate_from_field" ? syncConfigTvaRatePath : undefined,
        },
      },
      amountPath: syncMetadataSelectedAmountPath,
      amountFallbackPaths: syncConfigAmountFallbackPaths,
      amountMin: 0,
      dueDateBasePath: syncConfigDueDateBasePath,
      dueDateOffsetDays: syncConfigDueDateOffsetDays,
      dueDateOffsetFromSupplierPaymentTerms: false,
      supplierIdPath: "supplierId",
      type: "ponctuel",
      tauxTVA: syncConfigTvaMode === "fixed_rate" ? syncConfigTauxTVA : 0,
    };

    try {
      setSyncSaveLoading(true);
      if (syncSelectedRuleId) {
        const updated = await updateCashFlowSyncRule(syncSelectedRuleId, payload);
        message.success("Règle mise à jour");
        const rules = await getCashFlowSyncRules();
        setSyncRules(Array.isArray(rules) ? rules : []);
        if (updated?._id) setSyncSelectedRuleId(String(updated._id));
      } else {
        const created = await createCashFlowSyncRule(payload);
        message.success("Règle enregistrée");
        const rules = await getCashFlowSyncRules();
        setSyncRules(Array.isArray(rules) ? rules : []);
        if (created?._id) setSyncSelectedRuleId(String(created._id));
      }

      // Retour à la liste après save
      setSyncViewMode("list");
    } catch (e: any) {
      message.error(`Erreur enregistrement: ${e?.message || e}`);
    } finally {
      setSyncSaveLoading(false);
    }
  };

  const syncRulesColumns = [
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      render: (v: any) => String(v || ""),
    },
    {
      title: "Source",
      dataIndex: "sourceModel",
      key: "sourceModel",
      width: 160,
      render: (v: any) => String(v || ""),
    },
    {
      title: "Catégorie",
      dataIndex: "category",
      key: "category",
      width: 170,
      render: (v: any) => String(v || ""),
    },
    {
      title: "Actif",
      dataIndex: "enabled",
      key: "enabled",
      width: 90,
      render: (v: any) => (v === false ? "Non" : "Oui"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      render: (_: any, record: APICashFlowSyncRule) => {
        const id = record?._id ? String(record._id) : "";
        return (
          <Space>
            <Button
              size="small"
              onClick={() => startEditSyncRule(record)}
              disabled={!id}
            >
              Modifier
            </Button>
            <Popconfirm
              title="Supprimer cette synchronisation ?"
              okText="Supprimer"
              cancelText="Annuler"
              onConfirm={() => id && deleteSelectedSyncRule(id)}
            >
              <Button size="small" danger disabled={!id}>
                Supprimer
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const syncPreviewColumns = [
    {
      title: "Libellé",
      dataIndex: "libelle",
      key: "libelle",
      render: (v: any) => String(v || ""),
    },
    {
      title: "Montant",
      dataIndex: "montantTTC",
      key: "montantTTC",
      width: 140,
      render: (v: any) => `${Number(v || 0).toLocaleString("fr-FR")} €`,
    },
    {
      title: "Échéance",
      dataIndex: "echeance",
      key: "echeance",
      width: 140,
      render: (v: any) => (v ? dayjs(v).format("DD/MM/YYYY") : ""),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 220,
      render: (_: any, record: APICashFlowSyncProposal) =>
        `${record?.source?.model || ""}#${record?.source?.id || ""}`,
    },
  ];

  const previewExampleFromConfig = async () => {
    if (!syncMetadataSelectedModel) {
      message.error("Sélectionne un modèle source");
      return;
    }
    if (!syncMetadataSelectedAmountPath) {
      message.error("Sélectionne un champ montant");
      return;
    }
    if (!syncConfigTitleTemplate.trim()) {
      message.error("Le template de libellé est requis");
      return;
    }

    try {
      setSyncPreviewLoading(true);
      const resp = await previewCashFlowSyncExample({
        sourceModel: syncMetadataSelectedModel,
        category: syncConfigCategory,
        isRevenue: syncConfigIsRevenue,
        titleTemplate: syncConfigTitleTemplate,
        amountPath: syncMetadataSelectedAmountPath,
        amountFallbackPaths: syncConfigAmountFallbackPaths,
        dueDateBasePath: syncConfigDueDateBasePath,
        dueDateBasePaths: syncConfigDueDateBasePaths,
        dueDateTransform: syncConfigDueDateTransform,
        dueDateOffsetDays: syncConfigDueDateOffsetDays,
        tauxTVA: syncConfigTvaMode === "fixed_rate" ? syncConfigTauxTVA : 0,
        tvaMode: syncConfigTvaMode,
        tvaRatePath: syncConfigTvaRatePath,
        offsetMode: syncConfigOffsetMode,
        offsetDaysPath: syncConfigOffsetDaysPath,
      });

      const proposal = resp?.proposal || null;
      setSyncPreview(proposal ? [proposal] : []);
      if (!proposal) {
        message.warning("Aucun document source trouvé ou champs invalides (exemple vide)");
      }
    } catch (e: any) {
      message.error(`Erreur preview: ${e?.message || e}`);
    } finally {
      setSyncPreviewLoading(false);
    }
  };

  const sortedCashFlowItems = useMemo(() => {
    const sortByMonthTypeDateLabel = (a: CashFlowItem, b: CashFlowItem) => {
      const monthA = getItemMonthKey(a);
      const monthB = getItemMonthKey(b);

      if (monthA !== monthB) {
        return monthA.localeCompare(monthB);
      }

      if (a.type !== b.type) {
        if (a.type === CashFlowItemType.RECURRENT) return -1;
        if (b.type === CashFlowItemType.RECURRENT) return 1;
      }

      const dateA = getItemReferenceDate(a);
      const dateB = getItemReferenceDate(b);

      if (dateA && dateB) {
        const diff = dateA.diff(dateB);
        if (diff !== 0) return diff;
      }

      return (a.libelle || "").localeCompare(b.libelle || "");
    };

    return [...cashFlowItems].sort(sortByMonthTypeDateLabel);
  }, [cashFlowItems]);

  // Catégoriser les éléments
  const categorizedItems = useMemo(() => {
    return groupByCategory(cashFlowItems, selectedItems);
  }, [cashFlowItems, selectedItems]);

  const recentHistoryEvents = useMemo(() => {
    const cutoff = dayjs().subtract(HISTORY_WINDOW_DAYS, "day");
    return historyEvents
      .filter((ev) => dayjs(ev.timestamp).isAfter(cutoff))
      .sort((a, b) =>
        dayjs(b.timestamp).valueOf() - dayjs(a.timestamp).valueOf()
      );
  }, [historyEvents]);

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs(), // Date de début = aujourd'hui
    dayjs().add(30, "day"), // Date de fin = aujourd'hui + 30 jours
  ]);

  // Calcul du solde cumulé
  const [balanceData, setBalanceData] = useState<BalanceDataPoint[]>([]);
  const [selectedItem, setSelectedItem] = useState<CashFlowItem | null>(null);
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  // État pour la modale de catégorie
  const [categoryModalData, setCategoryModalData] =
    useState<CategoryModalData | null>(null);
  const [categoryModalVisible, setCategoryModalVisible] =
    useState<boolean>(false);

  // Proposals de synchronisation (non persistées) pour la modale catégorie
  const [categorySyncProposalsLoading, setCategorySyncProposalsLoading] = useState(false);
  const [categorySyncProposals, setCategorySyncProposals] = useState<APICashFlowSyncProposal[]>([]);
  const [categorySyncSavingHash, setCategorySyncSavingHash] = useState<string | null>(null);

  const categorySyncCarouselRef = useRef<HTMLDivElement | null>(null);

  // État pour la modale TVA déjà déclaré plus haut

  // État pour la modale de création
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
  const [createFormData, setCreateFormData] =
    useState<CreateItemFormData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Chargement des données depuis l'API au montage du composant
  useEffect(() => {
    const loadCashFlowItems = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const data = await getAllCashFlowItems();

        // Adapter les données de l'API au format attendu par le composant
        const adaptedData = data.map((item) => ({
          ...item,
          id: item._id, // Assurer la compatibilité avec le code existant
          categorie: determinerCategorie(item.category, item.isRevenue),
        })) as CashFlowItem[];

        setCashFlowItems(adaptedData);

        // Générer automatiquement les données TVA après le chargement des items
        const tvaResult = generateTVAData(
          adaptedData,
          selectedItems,
          tvaOverrides
        );
        setTVAResult(tvaResult);
        setTVAData(tvaResult.months);
      } catch (error) {
        console.error(
          "Erreur lors du chargement des opérations financières:",
          error
        );
        setLoadError("Impossible de charger les données. Veuillez réessayer.");
      } finally {
        setIsLoading(false);
      }
    };

    loadCashFlowItems();
  }, []);

  // Chargement initial de l'historique depuis l'API
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const events = await getCashFlowHistory(10);
        setHistoryEvents(
          events.map((ev) => ({
            id: ev._id,
            type: ev.type as CashFlowHistoryEvent["type"],
            timestamp: String(ev.createdAt || ev.updatedAt || ""),
            title: ev.title,
            details: ev.details || "",
            metadata: ev.metadata,
          }))
        );
      } catch (error) {
        console.error(
          "Erreur lors du chargement de l'historique de cash flow:",
          error
        );
      }
    };

    loadHistory();
  }, []);

  // Mise à jour du solde
  useEffect(() => {
    const today = dayjs();

    // Filtrer les cash flows en fonction de leur statut de sélection ET des dates
    let filteredItems = sortedCashFlowItems
      .filter((item) => item._id && selectedItems[item._id] !== false)
      // Exclure les cashflows ponctuels dont l'échéance est passée
      .filter((item) => {
        // Pour les cashflows ponctuels, ne pas inclure ceux dont l'échéance est passée
        if (item.type === CashFlowItemType.PONCTUEL && item.echeance) {
          return !dayjs(item.echeance).isBefore(today, "day");
        }

        // Pour les cashflows récurrents, ne pas inclure ceux dont la date de fin est passée
        if (item.type === CashFlowItemType.RECURRENT && item.dateFin) {
          return !dayjs(item.dateFin).isBefore(today, "day");
        }

        // Inclure les autres cashflows
        return true;
      });

    // Ajouter les opérations TVA si elles existent
    if (tvaResult && tvaResult.operations && tvaResult.operations.length > 0) {
      // Filtrer les opérations TVA sélectionnées
      const tvaItems = tvaResult.operations.filter(
        (item) => item._id && selectedItems[item._id] !== false
      );
      if (tvaItems.length > 0) {
        filteredItems = [...filteredItems, ...tvaItems];
      }
    }

    const newBalanceData = calculateCumulativeBalance(
      filteredItems,
      initialBalance,
      dateRange
    );
    setBalanceData(newBalanceData);

    if (newBalanceData.length > 0) {
      const lastPoint = newBalanceData[newBalanceData.length - 1];
      dispatch(setCurrentBalance(lastPoint.balance));
    } else {
      dispatch(setCurrentBalance(initialBalance));
    }
  }, [
    initialBalance,
    sortedCashFlowItems,
    selectedItems,
    dateRange,
    tvaResult,
  ]); // Ajout de tvaResult pour re-render quand les opérations TVA changent

  const handleModalClose = () => {
    setSelectedItem(null);
    setModalVisible(false);
  };

  // Gérer l'ouverture de la modale de catégorie
  const handleCategoryClick = async (category: string) => {
    const today = dayjs();
    const tenDaysAgo = today.subtract(10, "day");

    const allCategoryItems = cashFlowItems
      .filter((item) => item.categorie === category)
      .filter((item) => {
        if (item.type === CashFlowItemType.PONCTUEL && item.echeance) {
          const echeanceDate = dayjs(item.echeance);
          if (echeanceDate.isBefore(tenDaysAgo, "day")) {
            return false;
          }
          return true;
        }

        if (item.type === CashFlowItemType.RECURRENT && item.dateFin) {
          return !dayjs(item.dateFin).isBefore(today, "day");
        }

        return true;
      });

    const isRevenue = category.includes("revenu");
    setCategoryModalData({ category, items: allCategoryItems, isRevenue });
    setCategoryModalVisible(true);

    // Charger les proposals de synchronisation pour cette catégorie (non persistées)
    try {
      setCategorySyncProposalsLoading(true);
      const resp = await getCashFlowSyncProposals(category, 50);
      const proposals = Array.isArray(resp?.proposals) ? resp.proposals : [];
      setCategorySyncProposals(proposals);
    } catch (e: any) {
      setCategorySyncProposals([]);
      message.error(`Erreur chargement propositions: ${e?.message || e}`);
    } finally {
      setCategorySyncProposalsLoading(false);
    }

    // Préserver l'état des cases déjà configurées - ne toucher QUE les nouveaux items
    setSelectedItems((prevSelectedItems) => {
      const updatedItems = { ...prevSelectedItems };
      allCategoryItems.forEach((item) => {
        // Ne modifie que les items qui n'ont pas encore d'état défini
        if (item.id && updatedItems[item.id] === undefined) {
          updatedItems[item.id] = true; // Par défaut, nouveaux items cochés
        }
      });
      return updatedItems;
    });
  };

  // Gérer la fermeture de la modale de catégorie
  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    setCategoryModalVisible(false);
    setCategorySyncProposals([]);
    setCategorySyncSavingHash(null);
  };

  const applySingleCategorySyncProposal = async (proposal: APICashFlowSyncProposal) => {
    if (!categoryModalData) return;
    const h = proposal?.source?.hash;
    if (!h) return;
    if (proposal.alreadyExists) return;

    try {
      setCategorySyncSavingHash(h);
      const resp = await applyCashFlowSyncProposals(categoryModalData.category, [h]);
      message.success(`Sauvegardé: ${resp.created} créé`);

      const createdItems = Array.isArray(resp?.items) ? resp.items : [];
      const sourceModel = String(proposal?.source?.model || "");
      const createdFirstId = createdItems[0]?._id;

      if (resp?.created > 0 && createdFirstId && sourceModel.toLowerCase().includes("ticket")) {
        try {
          await logCashFlowHistoryEvent({
            cashFlowId: String(createdFirstId),
            type: "ticket_sync",
            title: "Synchronisation ticket",
            details: String(proposal?.libelle || ""),
            metadata: {
              source: proposal.source,
            },
          });
        } catch (e) {
          // ignore
        }
      }

      // Refresh cashflow list
      const data = await getAllCashFlowItems();
      const adaptedData = data.map((item) => ({
        ...item,
        id: item._id,
        categorie: determinerCategorie(item.category, item.isRevenue),
      })) as CashFlowItem[];
      setCashFlowItems(adaptedData);

      // Refresh proposals list
      const proposalsResp = await getCashFlowSyncProposals(categoryModalData.category, 50);
      const proposals = Array.isArray(proposalsResp?.proposals) ? proposalsResp.proposals : [];
      setCategorySyncProposals(proposals);
    } catch (e: any) {
      message.error(`Erreur sauvegarde: ${e?.message || e}`);
    } finally {
      setCategorySyncSavingHash(null);
    }
  };

  const handleArchiveClick = (item: CashFlowItem) => {
    // Stocker l'item à archiver
    setArchivingItem(item);
    // Ouvrir le tooltip d'archivage
    setArchiveTooltipVisible(true);
    // Par défaut, date de fin = un mois après aujourd'hui
    setArchiveDate(dayjs().add(1, "month"));
  };

  // Gérer la fermeture du tooltip d'archivage
  const handleArchiveTooltipClose = () => {
    setArchivingItem(null);
    setArchiveTooltipVisible(false);
  };

  // Gérer la soumission du formulaire d'archivage
  const handleArchiveSubmit = async (
    editingItem: CashFlowItem,
    dateFin: dayjs.Dayjs
  ) => {
    if (!editingItem._id) {
      message.error("L'élément à archiver n'a pas d'ID");
      return;
    }
    try {
      setIsLoading(true);

      // Préparer un objet complet avec tous les champs requis
      // Convertir les dates string en objets dayjs pour le format attendu
      const updateData: APICashFlowFormData = {
        category: editingItem.category,
        isRevenue: editingItem.isRevenue,
        libelle: editingItem.libelle,
        montantHT: editingItem.montantHT,
        montantTTC: editingItem.montantTTC,
        tva: editingItem.tva,
        tauxTVA: editingItem.tauxTVA as 0 | 5.5 | 10 | 20,
        type: editingItem.type,
        dateDebut: dayjs(editingItem.dateDebut),
        dateFin: dateFin, // Nouvelle date de fin (archivage)
        jourDuMois: editingItem.jourDuMois,
      };

      // Si c'est une opération ponctuelle avec échéance, l'ajouter
      if (
        editingItem.type === CashFlowItemType.PONCTUEL &&
        editingItem.echeance
      ) {
        updateData.echeance = dayjs(editingItem.echeance);
      }

      // Appel API pour mettre à jour l'élément avec tous les champs
      const updatedItem = await updateCashFlowItem(editingItem._id, updateData);

      // Mettre à jour la liste des éléments localement
      setCashFlowItems((prev) =>
        prev.map((item) =>
          item._id === updatedItem._id
            ? { ...updatedItem, categorie: updatedItem.category }
            : item
        )
      );

      pushHistoryEvent(
        {
          type: "archive",
          title: "Archivage d'une opération récurrente",
          details: `${formatCashFlowItemKind(
            editingItem
          )} • fin de la récurrence au ${dateFin.format("DD/MM/YYYY")}`,
        },
        {
          cashFlowId: editingItem._id,
          metadata: {
            previousDateFin: editingItem.dateFin,
            newDateFin: dateFin.format("YYYY-MM-DD"),
            itemLabel: editingItem.libelle,
            itemType: editingItem.type,
          },
        }
      );
      message.success(
        "Élément archivé avec succès. Il ne sera plus visible une fois la date de fin atteinte."
      );
      handleArchiveTooltipClose();
    } catch (error) {
      message.error(
        `Erreur lors de l'archivage : ${
          error instanceof Error ? error.message : "Erreur inconnue"
        }`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleTVAClick = () => {
    const result = generateTVAData(cashFlowItems, selectedItems, tvaOverrides);
    setTVAResult(result);
    setTVAData(result.months);
    setTVAModalVisible(true);
  };

  const handleTVAModalClose = () => {
    setTVAModalVisible(false);
  };

  const handleInitialBalanceChange = (value: number | null) => {
    const newVal = value ?? 0;
    const oldVal = initialBalance;

    if (newVal === oldVal) {
      return;
    }

    // Mettre à jour le solde initial dans le store immédiatement
    dispatch(setInitialBalance(newVal));

    // Mémoriser la première valeur (previous) et la dernière (next)
    if (!initialBalanceLastValuesRef.current) {
      initialBalanceLastValuesRef.current = {
        previous: oldVal,
        next: newVal,
      };
    } else {
      initialBalanceLastValuesRef.current = {
        previous: initialBalanceLastValuesRef.current.previous,
        next: newVal,
      };
    }

    // Debounce du pushHistoryEvent pour éviter un enregistrement à chaque frappe
    if (initialBalanceTimerRef.current !== undefined) {
      window.clearTimeout(initialBalanceTimerRef.current);
    }

    initialBalanceTimerRef.current = window.setTimeout(() => {
      const values = initialBalanceLastValuesRef.current;
      const previous = values?.previous ?? oldVal;
      const next = values?.next ?? newVal;

      pushHistoryEvent(
        {
          type: "initial_balance_change",
          title: "Modification du solde initial",
          details: `${previous.toLocaleString(
            "fr-FR"
          )} € → ${next.toLocaleString("fr-FR")} €`,
        },
        {
          metadata: {
            previousValue: previous,
            newValue: next,
          },
        }
      );

      // Réinitialiser le contexte de debounce
      initialBalanceLastValuesRef.current = null;
      initialBalanceTimerRef.current = undefined;
    }, 2000);
  };

  const handleTVAOverrideChange = (
    monthLabel: string,
    value: number | null
  ) => {
    const normalized = typeof value === "number" ? value : 0;
    const previousMonth = tvaData.find((m) => m.mois === monthLabel);
    // Valeur de référence pour l'historique : toujours le solde calculé par l'algo
    const previousBaseSolde = previousMonth ? previousMonth.soldeTVA : 0;
    const newOverrides: TVAOverrides = {
      ...tvaOverrides,
      [monthLabel]: normalized,
    };
    setTVAOverrides(newOverrides);
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          "cashflow_tva_overrides",
          JSON.stringify(newOverrides)
        );
      }
    } catch {
      // ignore storage errors
    }
    const result = generateTVAData(cashFlowItems, selectedItems, newOverrides);
    setTVAResult(result);
    setTVAData(result.months);

    if (previousMonth) {
      // Mémoriser les valeurs pour ce mois
      tvaOverrideLastValuesRef.current[monthLabel] = {
        previous: previousBaseSolde,
        next: normalized,
      };

      // Debounce du pushHistoryEvent pour éviter un enregistrement à chaque frappe
      const timers = tvaOverrideTimersRef.current;
      if (timers[monthLabel] !== undefined) {
        window.clearTimeout(timers[monthLabel]);
      }

      timers[monthLabel] = window.setTimeout(() => {
        const values = tvaOverrideLastValuesRef.current[monthLabel];
        const previous = values?.previous ?? previousBaseSolde;
        const next = values?.next ?? normalized;

        pushHistoryEvent(
          {
            type: "tva_override",
            title: `Ajustement TVA ${monthLabel}`,
            details: `${previous.toLocaleString(
              "fr-FR"
            )} € → ${next.toLocaleString("fr-FR")} €`,
          },
          {
            metadata: {
              monthLabel,
              previousValue: previous,
              newValue: next,
            },
          }
        );

        // Nettoyage du timer une fois exécuté
        delete tvaOverrideTimersRef.current[monthLabel];
      }, 2000);
    }
  };

  const handleItemCheckChange = (
    itemId: string | undefined,
    checked: boolean
  ) => {
    if (!itemId) return;
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: checked,
    }));
  };

  type CheckboxChangeEvent = {
    target: {
      checked: boolean;
    };
  };

  const handleOverdueEcheanceChange = async (
    item: CashFlowItem,
    date: dayjs.Dayjs | null
  ) => {
    if (!item._id || !date) {
      return;
    }
    try {
      setIsLoading(true);
      const oldEcheance = item.echeance;
      const updateData: Partial<APICashFlowFormData> = {
        category: item.category,
        isRevenue: item.isRevenue,
        libelle: item.libelle,
        montantHT: item.montantHT,
        montantTTC: item.montantTTC,
        tva: item.tva,
        tauxTVA:
          (item.tauxTVA !== undefined
            ? (item.tauxTVA as 0 | 5.5 | 10 | 20)
            : 20),
        type: CashFlowItemType.PONCTUEL,
        echeance: date,
        dateDebut: dayjs(item.dateDebut),
      };
      const updatedItem = await updateCashFlowItem(item._id, updateData);
      setCashFlowItems((prev) =>
        prev.map((it) =>
          it._id === updatedItem._id
            ? {
                ...updatedItem,
                id: updatedItem._id,
                categorie: determinerCategorie(
                  updatedItem.category,
                  updatedItem.isRevenue
                ),
              }
            : it
        )
      );
      setCategoryModalData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((it) =>
            it._id === updatedItem._id
              ? {
                  ...updatedItem,
                  id: updatedItem._id,
                  categorie: determinerCategorie(
                    updatedItem.category,
                    updatedItem.isRevenue
                  ),
                }
              : it
          ),
        };
      });
      pushHistoryEvent(
        {
          type: "echeance_update",
          title: "Mise à jour de l'échéance d'une opération",
          details: `Replanification de l'échéance pour ${formatCashFlowItemKind(
            item
          )} : ${
            oldEcheance ? dayjs(oldEcheance).format("DD/MM/YYYY") : "(aucune)"
          } → ${date.format("DD/MM/YYYY")}`,
        },
        {
          cashFlowId: item._id,
          metadata: {
            previousEcheance: oldEcheance,
            newEcheance: date.format("YYYY-MM-DD"),
            itemLabel: updatedItem.libelle,
            itemType: updatedItem.type,
          },
        }
      );
      message.success("Échéance mise à jour");
    } catch (error) {
      message.error(
        `Erreur lors de la mise à jour de l'échéance : ${
          error instanceof Error ? error.message : "Erreur inconnue"
        }`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddItem = () => {
    if (!categoryModalData) return;

    const today = dayjs();

    setCreateFormData({
      category: categoryModalData.category,
      isRevenue: categoryModalData.isRevenue,
      libelle: "",
      montantHT: 0,
      montantTTC: 0,
      tva: 0,
      tauxTVA: 20,
      type: CashFlowItemType.RECURRENT,
      echeance: today.format("YYYY-MM-DD"),
      jourDuMois: today.date(),
      inputMode: "TTC",
    });

    // Fermer la modale de catégorie et ouvrir la modale de création
    setCategoryModalVisible(false);
    setCreateModalVisible(true);
  };

  // Handler pour le bouton d'édition
  const handleEditClick = (item: CashFlowItem) => {
    // Stocker l'item à éditer
    setEditingItem(item);

    // Date d'aujourd'hui pour l'échéance par défaut si nécessaire
    const today = dayjs();

    // Préparer les données pour le formulaire
    const formValues: CreateItemFormData = {
      category: item.category,
      isRevenue: item.isRevenue,
      libelle: item.libelle,
      montantTTC: item.montantTTC || 0,
      montantHT: item.montantHT || 0,
      tva: item.tva || 0,
      tauxTVA:
        (item.tauxTVA !== undefined
          ? (item.tauxTVA as 0 | 5.5 | 10 | 20)
          : 20),
      type: item.type,
      inputMode: "TTC" as "TTC", // Par défaut en mode TTC
      echeance: item.echeance || today.format("YYYY-MM-DD"),
    };

    // Si c'est une opération récurrente, on ajoute le jour du mois
    if (item.jourDuMois) {
      formValues.jourDuMois = item.jourDuMois;
    }

    // Initialiser les données du formulaire d'édition
    setCreateFormData(formValues);

    // Ouvrir la modal pour édition (on réutilise la modal de création)
    setCreateModalVisible(true);

    // Indiquer qu'on est en mode édition
    setCategoryModalVisible(false);
  };

  // Handler pour confirmer la suppression
  const handleConfirmDelete = async (itemId: string) => {
    const itemToDelete = cashFlowItems.find((item) => item._id === itemId);
    try {
      setIsLoading(true);

      // Appel API pour supprimer l'élément
      await deleteCashFlowItem(itemId);

      // Mettre à jour la liste des éléments localement
      setCashFlowItems((prev) => prev.filter((item) => item._id !== itemId));

      // Message de succès
      message.success("Élément supprimé avec succès");

      if (itemToDelete) {
        pushHistoryEvent(
          {
            type: "delete",
            title: "Suppression d'une opération",
            details: `Suppression de ${formatCashFlowItemKind(itemToDelete)}`,
          },
          {
            cashFlowId: itemToDelete._id,
            metadata: {
              montantTTC: itemToDelete.montantTTC,
              categorie: itemToDelete.categorie,
              itemLabel: itemToDelete.libelle,
              itemType: itemToDelete.type,
            },
          }
        );
      }

      // Fermer la modale catégorie si elle est ouverte
      if (categoryModalVisible) {
        setCategoryModalVisible(false);
      }
    } catch (error) {
      message.error(
        `Erreur lors de la suppression : ${
          error instanceof Error ? error.message : "Erreur inconnue"
        }`
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handler pour soumettre le formulaire d'édition
  const handleEditFormSubmit = async () => {
    if (!editingItem || !editingItem._id) {
      message.error("Impossible de mettre à jour l'élément : ID manquant");
      return;
    }

    if (!createFormData) {
      message.error("Aucune donnée de formulaire disponible");
      return;
    }

    try {
      setIsLoading(true);

      // Préparer les données pour l'API
      const formData: APICashFlowFormData = {
        category: createFormData.category,
        libelle: createFormData.libelle,
        montantTTC: createFormData.montantTTC,
        montantHT: createFormData.montantHT,
        tva: createFormData.tva,
        tauxTVA: createFormData.tauxTVA,
        type: createFormData.type,
        isRevenue: createFormData.isRevenue,
        dateDebut: dayjs(), // Date de mise à jour
      };

      // Ajouter les champs conditionnels selon le type
      if (createFormData.type === CashFlowItemType.PONCTUEL) {
        formData.echeance = dayjs(createFormData.echeance);
      } else if (
        createFormData.type === CashFlowItemType.RECURRENT &&
        createFormData.jourDuMois
      ) {
        formData.jourDuMois = createFormData.jourDuMois;
      }

      // Appel API pour mettre à jour l'élément
      const updatedItem = await updateCashFlowItem(editingItem._id, formData);

      const adaptedItem: CashFlowItem = {
        ...updatedItem,
        id: updatedItem._id,
        categorie: determinerCategorie(
          updatedItem.category,
          updatedItem.isRevenue
        ),
      };

      setCashFlowItems((prev) =>
        prev.map((item) => (item._id === adaptedItem._id ? adaptedItem : item))
      );

      setCategoryModalData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item._id === adaptedItem._id ? adaptedItem : item
          ),
        };
      });

      const oldItem = editingItem;
      const newItem = adaptedItem;

      // 1) Modification du montant TTC
      if (oldItem.montantTTC !== newItem.montantTTC) {
        pushHistoryEvent(
          {
            type: "update",
            title: "Modification du montant",
            details: `${oldItem.montantTTC.toLocaleString(
              "fr-FR"
            )} € -> ${newItem.montantTTC.toLocaleString("fr-FR")} €`,
          },
          {
            cashFlowId: editingItem._id,
            metadata: {
              previousMontantTTC: oldItem.montantTTC,
              newMontantTTC: newItem.montantTTC,
              itemLabel: newItem.libelle,
              itemType: newItem.type,
            },
          }
        );
      }

      // 2) Modification de la date d'échéance (uniquement pour les opérations ponctuelles)
      const oldEcheance = oldItem.echeance;
      const newEcheance = newItem.echeance;
      if (
        oldItem.type === CashFlowItemType.PONCTUEL &&
        oldEcheance !== newEcheance
      ) {
        pushHistoryEvent(
          {
            type: "echeance_update",
            title: "Modification de la date d'échéance",
            details: `${formatShortDateLabel(
              oldEcheance || "(aucune)"
            )} -> ${formatShortDateLabel(newEcheance || "(aucune)")}`,
          },
          {
            cashFlowId: editingItem._id,
            metadata: {
              previousEcheance: oldEcheance,
              newEcheance: newEcheance,
              itemLabel: newItem.libelle,
              itemType: newItem.type,
            },
          }
        );
      }

      // 3) Modification du taux de TVA (changement de taux uniquement)
      if (oldItem.tauxTVA !== newItem.tauxTVA) {
        pushHistoryEvent(
          {
            type: "update",
            title: "Modification de la TVA",
            details: `${oldItem.tauxTVA ?? 0}% -> ${newItem.tauxTVA ?? 0}%`,
          },
          {
            cashFlowId: editingItem._id,
            metadata: {
              previousTauxTVA: oldItem.tauxTVA,
              newTauxTVA: newItem.tauxTVA,
              itemLabel: newItem.libelle,
              itemType: newItem.type,
            },
          }
        );
      }

      // 4) Modification du jour de prélèvement pour les récurrents
      const oldJour = oldItem.jourDuMois;
      const newJour = newItem.jourDuMois;
      if (oldJour !== newJour) {
        pushHistoryEvent(
          {
            type: "update",
            title: "Modification du jour de prélèvement",
            details: `${oldJour ?? "-"} -> ${newJour ?? "-"} jour du mois`,
          },
          {
            cashFlowId: editingItem._id,
            metadata: {
              previousJourDuMois: oldJour,
              newJourDuMois: newJour,
              itemLabel: newItem.libelle,
              itemType: newItem.type,
            },
          }
        );
      }
      // 5) Si aucun des quatre points clés n'a changé mais qu'il y a d'autres modifs, garder un ticket générique
      const otherChanged =
        oldItem.libelle !== newItem.libelle ||
        oldItem.categorie !== newItem.categorie ||
        oldItem.isRevenue !== newItem.isRevenue ||
        oldItem.type !== newItem.type ||
        oldItem.montantHT !== newItem.montantHT ||
        oldItem.tva !== newItem.tva ||
        oldJour !== newJour;

      if (
        otherChanged &&
        oldItem.montantTTC === newItem.montantTTC &&
        oldEcheance === newEcheance &&
        oldItem.tauxTVA === newItem.tauxTVA &&
        oldJour === newJour
      ) {
        pushHistoryEvent(
          {
            type: "update",
            title: "Modification d'une opération",
            details: buildUpdateHistoryDetails(oldItem, newItem),
          },
          {
            cashFlowId: editingItem._id,
            metadata: {
              itemLabel: newItem.libelle,
              itemType: newItem.type,
            },
          }
        );
      }

      message.success("Élément mis à jour avec succès");
      setCreateModalVisible(false);
      setEditingItem(null);
      setCreateFormData(null);
    } catch (error) {
      message.error(
        `Erreur lors de la mise à jour : ${
          error instanceof Error ? error.message : "Erreur inconnue"
        }`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateModalClose = () => {
    setCreateModalVisible(false);
    setCreateFormData(null);
  };

  const handleCreateFormSubmit = async () => {
    if (!createFormData) return;

    try {
      // Préparation des données au format attendu par l'API
      const apiFormData: APICashFlowFormData = {
        category: createFormData.category,
        isRevenue: createFormData.isRevenue,
        libelle: createFormData.libelle,
        montantHT: createFormData.montantHT,
        montantTTC: createFormData.montantTTC,
        tva: createFormData.tva,
        tauxTVA: createFormData.tauxTVA,
        type: createFormData.type as any, // Conversion nécessaire entre les types
        dateDebut: dayjs(createFormData.echeance), // Date de début = date d'échéance par défaut
        jourDuMois:
          createFormData.type === CashFlowItemType.RECURRENT
            ? createFormData.jourDuMois
            : undefined,
      };

      // Si c'est une opération ponctuelle, on utilise la date d'échéance
      if (createFormData.type === CashFlowItemType.PONCTUEL) {
        apiFormData.echeance = dayjs(createFormData.echeance);
      }

      // Appel de l'API pour enregistrer en base de données
      setLoading(true);
      const result = await createCashFlowItem(apiFormData);

      // Ajout à la liste locale
      const newItem: CashFlowItem = {
        id: result._id || "",
        _id: result._id,
        categorie: createFormData.category,
        category: createFormData.category,
        isRevenue: createFormData.isRevenue,
        libelle: result.libelle,
        montantHT: result.montantHT,
        montantTTC: result.montantTTC,
        tva: result.tva,
        tauxTVA: result.tauxTVA,
        type: result.type as CashFlowItemType,
        echeance: result.echeance || result.dateDebut || "",
        dateDebut: result.dateDebut || "",
        jourDuMois: result.jourDuMois,
      };

      // Mise à jour de la liste des cash flow items
      setCashFlowItems((prevItems) => [...prevItems, newItem]);

      pushHistoryEvent(
        {
          type: "create",
          title: createFormData.isRevenue
            ? "Création d'un revenu"
            : "Création d'une dépense",
          details: formatCashFlowItemKind(newItem),
        },
        {
          cashFlowId: newItem._id,
          metadata: {
            categorie: newItem.categorie,
            montantTTC: newItem.montantTTC,
            isRevenue: newItem.isRevenue,
            itemLabel: newItem.libelle,
            itemType: newItem.type,
            tauxTVA: newItem.tauxTVA,
            echeance: newItem.echeance,
            jourDuMois: newItem.jourDuMois,
          },
        }
      );

      message.success(
        `${createFormData.isRevenue ? "Revenu" : "Dépense"} ajouté avec succès!`
      );
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'opération:", error);
      message.error(
        `Erreur lors de l'ajout: ${
          error instanceof Error ? error.message : "Erreur inconnue"
        }`
      );
    } finally {
      setLoading(false);
      // Fermer la modale
      handleCreateModalClose();
    }
  };

  // Interface pour les infos TVA dans le tooltip
  interface TVASoldeInfo {
    type: string;
    mois: string;
    montant: number;
    isCredit: boolean;
    dateEcheance: string;
  }

  // Composant CustomTooltip pour le graphique Recharts
  const CustomTooltip: React.FC<CustomTooltipProps> = ({
    active,
    payload,
    label,
  }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value as number;

      // Recherche des opérations du jour
      const dateLabel = label;
      const dateDuTooltip = dayjs(dateLabel);

      // Rechercher s'il y a une opération TVA (solde) pour cette date
      let tvaSoldeInfo: TVASoldeInfo | null = null;
      if (tvaData && tvaData.length > 0) {
        // Parcourir les mois de TVA
        for (const monthData of tvaData) {
          const monthObj = dayjs(monthData.mois, "MMMM YYYY");

          // Déterminer la date d'échéance en fonction du signe du solde
          if (monthData.soldeTVA !== 0) {
            let dateEcheance;
            let tvaType = "";

            if (monthData.soldeTVA > 0) {
              // TVA à payer: 27 du mois M+1
              dateEcheance = monthObj.add(1, "month").date(27);
              tvaType = "TVA à payer";
            } else {
              // Crédit de TVA: 5 du mois M+2
              dateEcheance = monthObj.add(2, "month").date(5);
              tvaType = "Crédit de TVA";
            }

            // Vérifier si cette date d'échéance correspond à la date du tooltip
            if (dateEcheance.format("YYYY-MM-DD") === dateLabel) {
              tvaSoldeInfo = {
                type: tvaType,
                mois: monthData.mois,
                montant: Math.abs(monthData.soldeTVA),
                isCredit: monthData.soldeTVA < 0,
                dateEcheance: dateEcheance.format("DD/MM/YYYY"),
              };
              break;
            }
          }
        }
      }

      const operationsDuJour = cashFlowItems.filter((item: CashFlowItem) => {
        // Pour les opérations ponctuelles, vérifier si l'échéance correspond à cette date
        if (item.type === CashFlowItemType.PONCTUEL && item.echeance) {
          const dateEcheance = dayjs(item.echeance).format("YYYY-MM-DD");
          return dateEcheance === dateLabel;
        }

        // Pour les opérations récurrentes, vérifier si elles se produisent ce jour-là
        if (item.type === CashFlowItemType.RECURRENT && item.jourDuMois) {
          const jourDate = dateDuTooltip.date();
          return jourDate === item.jourDuMois;
        }

        return false;
      });

      // Ne montrer que les opérations sélectionnées (cochées)
      const operationsSelectionnees = operationsDuJour.filter(
        (item: CashFlowItem) =>
          !item._id || (item._id && selectedItems[item._id] !== false)
      );

      return (
        <div className="custom-recharts-tooltip">
          <div style={{ marginBottom: 8, borderBottom: "1px solid #f1f5f9", paddingBottom: 4 }}>
            <Text strong style={{ fontSize: 13, color: "#0f172a" }}>
              {dayjs(label).format("DD MMMM YYYY")}
            </Text>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Solde projeté</Text>
            <Text strong style={{ fontSize: 16, color: value >= 0 ? "#10b981" : "#ef4444" }}>
              {value.toLocaleString("fr-FR")} €
            </Text>
          </div>

          {/* Affichage des informations TVA s'il y en a pour cette date */}
          {tvaSoldeInfo && (
            <div
              style={{
                margin: "8px 0",
                padding: "8px",
                backgroundColor: tvaSoldeInfo.isCredit ? "#f0fdf4" : "#fef2f2",
                borderRadius: "8px",
                border: `1px solid ${tvaSoldeInfo.isCredit ? "#bbf7d0" : "#fecaca"}`,
              }}
            >
              <Text strong style={{ fontSize: 11, display: "block", color: tvaSoldeInfo.isCredit ? "#166534" : "#991b1b", textTransform: "uppercase" }}>
                {tvaSoldeInfo.type}
              </Text>
              <Text style={{ fontSize: 13, fontWeight: 600, color: tvaSoldeInfo.isCredit ? "#15803d" : "#dc2626" }}>
                {tvaSoldeInfo.isCredit ? "+" : "-"}{tvaSoldeInfo.montant.toLocaleString("fr-FR")} €
              </Text>
            </div>
          )}

          {operationsSelectionnees.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text strong style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 4 }}>
                Opérations du jour
              </Text>
              <div style={{ maxHeight: 150, overflowY: "auto" }}>
                {operationsSelectionnees.map((op: CashFlowItem, idx: number) => (
                  <div key={op._id || idx} style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 2 }}>
                    <Text style={{ fontSize: 12, color: "#334155", flex: 1 }}>{op.libelle}</Text>
                    <Text strong style={{ fontSize: 12, color: op.isRevenue ? "#10b981" : "#ef4444" }}>
                      {op.isRevenue ? "+" : "-"}{op.montantTTC.toLocaleString("fr-FR")}€
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const renderCashFlowSection = () => (
    <div className="cash-flow-section">
      <Card className="neo-card">
        <div className="filter-bar">
          <Row gutter={[24, 16]} align="middle">
            <Col xs={24} md={8}>
              <Space direction="vertical" size={2} style={{ width: "100%" }}>
                <Text strong style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase" }}>Solde initial</Text>
                <InputNumber
                  value={initialBalance}
                  onChange={handleInitialBalanceChange}
                  formatter={(value: number | undefined) =>
                    value ? `${value} €` : "0 €"
                  }
                  parser={(value: string | undefined) =>
                    Number((value || "0").replace(" €", ""))
                  }
                  style={{ width: "100%", fontWeight: 600 }}
                />
              </Space>
            </Col>
            <Col xs={24} md={10}>
              <Space direction="vertical" size={2} style={{ width: "100%" }}>
                <Text strong style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase" }}>Période d'analyse</Text>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      const today = dayjs().startOf("day");
                      const startDate = dates[0].isBefore(today) ? today : dates[0];
                      setDateRange([startDate, dates[1]]);
                    }
                  }}
                  disabledDate={(current) =>
                    current && current < dayjs().startOf("day")
                  }
                  format="DD/MM/YYYY"
                  style={{ width: "100%" }}
                />
              </Space>
            </Col>
            <Col xs={24} md={6} style={{ textAlign: "right", display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
              <Button 
                icon={<NodeIndexOutlined />} 
                onClick={openSyncModal}
                style={{ height: 40, fontWeight: 600 }}
              >
                Synchronisation
              </Button>
            </Col>
          </Row>
        </div>

        <Row gutter={[24, 24]}>
          {/* Colonne de cards à gauche */}
          <Col xs={24} lg={7}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, height: 420 }}>
              {/* Card Dépenses récurrentes */}
              <Card
                className="category-card expense-recurrent"
                onClick={() => handleCategoryClick("depense_recurrente")}
                bodyStyle={{ padding: "8px 16px" }}
                style={{ flex: 1 }}
              >
                <Statistic
                  title="Dépenses récurrentes"
                  value={
                    categorizedItems.depense_recurrente.reduce(
                      (sum, item) => sum + item.montantTTC,
                      0
                    )
                  }
                  precision={2}
                  suffix="€"
                  valueStyle={{ fontSize: 14 }}
                  prefix={<MinusOutlined style={{ fontSize: 12, color: "#ef4444" }} />}
                />
              </Card>

              {/* Card Dépenses ponctuelles */}
              <Card
                className="category-card expense-exception"
                onClick={() => handleCategoryClick("depense_exceptionnelle")}
                bodyStyle={{ padding: "8px 16px" }}
                style={{ flex: 1 }}
              >
                <Statistic
                  title="Dépenses exceptionnelles"
                  value={
                    categorizedItems.depense_exceptionnelle.reduce(
                      (sum, item) => sum + item.montantTTC,
                      0
                    )
                  }
                  precision={2}
                  suffix="€"
                  valueStyle={{ fontSize: 14 }}
                  prefix={<MinusOutlined style={{ fontSize: 12, color: "#f97316" }} />}
                />
              </Card>

              {/* Card Dépenses Ventes */}
              <Card
                className="category-card expense-sale"
                onClick={() => handleCategoryClick("depense_vente")}
                bodyStyle={{ padding: "8px 16px" }}
                style={{ flex: 1 }}
              >
                <Statistic
                  title="Dépenses ventes"
                  value={
                    categorizedItems.depense_vente.reduce(
                      (sum, item) => sum + item.montantTTC,
                      0
                    )
                  }
                  precision={2}
                  suffix="€"
                  valueStyle={{ fontSize: 14 }}
                  prefix={<MinusOutlined style={{ fontSize: 12, color: "#f59e0b" }} />}
                />
              </Card>

              {/* Card Revenus Ventes */}
              <Card
                className="category-card revenue-sale"
                onClick={() => handleCategoryClick("revenu_vente")}
                bodyStyle={{ padding: "8px 16px" }}
                style={{ flex: 1 }}
              >
                <Statistic
                  title="Revenus ventes"
                  value={categorizedItems.revenu_vente.reduce(
                    (sum, item) => sum + item.montantTTC,
                    0
                  )}
                  precision={2}
                  suffix="€"
                  valueStyle={{ fontSize: 14 }}
                  prefix={<PlusOutlined style={{ fontSize: 12, color: "#10b981" }} />}
                />
              </Card>

              {/* Card Revenus exceptionnels */}
              <Card
                className="category-card revenue-exception"
                onClick={() => handleCategoryClick("revenu_exceptionnel")}
                bodyStyle={{ padding: "8px 16px" }}
                style={{ flex: 1 }}
              >
                <Statistic
                  title="Revenus exceptionnels"
                  value={categorizedItems.revenu_exceptionnel.reduce(
                    (sum, item) => sum + item.montantTTC,
                    0
                  )}
                  precision={2}
                  suffix="€"
                  valueStyle={{ fontSize: 14 }}
                  prefix={<PlusOutlined style={{ fontSize: 12, color: "#06b6d4" }} />}
                />
              </Card>

              {/* Card TVA */}
              <Card
                className="category-card tva-card"
                onClick={handleTVAClick}
                bodyStyle={{ padding: "8px 16px" }}
                style={{ flex: 1 }}
              >
                {tvaResult?.prochaineMontantTVA ? (
                  <Statistic
                    title={
                      <span style={{ fontSize: 11 }}>
                        {tvaResult.prochaineMontantTVA.libelle}
                        <Tooltip title={`Échéance: ${tvaResult.prochaineMontantTVA.date}`}>
                          <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 10 }} />
                        </Tooltip>
                      </span>
                    }
                    value={tvaResult.prochaineMontantTVA.montant}
                    precision={2}
                    suffix="€"
                    valueStyle={{
                      fontSize: 14,
                      color: tvaResult.prochaineMontantTVA.libelle.startsWith("Crédit de TVA")
                        ? "#10b981"
                        : "#ef4444",
                    }}
                    prefix={
                      tvaResult.prochaineMontantTVA.libelle.startsWith("Crédit de TVA") ? (
                        <RiseOutlined style={{ fontSize: 12 }} />
                      ) : (
                        <FallOutlined style={{ fontSize: 12 }} />
                      )
                    }
                  />
                ) : (
                  <Statistic title="Synthèse TVA" value={0} precision={2} suffix="€" valueStyle={{ fontSize: 14 }} />
                )}
              </Card>
            </div>
          </Col>

          {/* Graphique à droite */}
          <Col xs={24} lg={17}>
            <div style={{ 
              background: "#ffffff", 
              padding: "24px", 
              borderRadius: "16px", 
              border: "1px solid #f1f5f9",
              height: 441,
              display: "flex",
              flexDirection: "column"
            }}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 16, color: "#0f172a" }}>Projection de trésorerie</Text>
                <Text type="secondary" style={{ display: "block", fontSize: 13 }}>Évolution du solde sur la période sélectionnée</Text>
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={balanceData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#64748b", fontSize: 12 }}
                      dy={10}
                      tickFormatter={(value: string) =>
                        dayjs(value).format("DD/MM")
                      }
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#64748b", fontSize: 12 }}
                      tickFormatter={(value: number) =>
                        `${value.toLocaleString("fr-FR")}€`
                      }
                    />
                    <RechartsTooltip
                      content={(props) => <CustomTooltip {...props} />}
                    />
                    <Line
                      type="monotone"
                      dataKey="balance"
                      stroke="#0f172a"
                      strokeWidth={3}
                      dot={false}
                      activeDot={{ r: 6, strokeWidth: 0, fill: "#0f172a" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Col>
        </Row>

        <div
          style={{ marginTop: "4rem" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <Title level={4} style={{ margin: 0 }}>Historique des modifications</Title>
            <Text type="secondary" style={{ fontSize: 13 }}>10 derniers jours</Text>
          </div>

          {recentHistoryEvents.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", background: "#f8fafc", borderRadius: 12, border: "1px dashed #e2e8f0" }}>
              <Text type="secondary">Aucune modification de cash flow récente.</Text>
            </div>
          ) : (
            <>
              <Carousel
                dots={false}
                arrows
                draggable
                className={"w-full"}
                infinite={false}
                slidesToShow={3}
                slidesToScroll={3}
                beforeChange={(_, to) => setHistoryCurrentSlide(Math.floor(to / 3))}
              >
                {recentHistoryEvents.map((event) => {
                  let borderColor = "#cbd5e1";
                  let backgroundColor = "#ffffff";
                  let icon = <InfoCircleOutlined />;

                  switch (event.type) {
                    case "create":
                      borderColor = "#10b981";
                      backgroundColor = "#f0fdf4";
                      icon = <PlusOutlined style={{ color: "#10b981" }} />;
                      break;
                    case "delete":
                      borderColor = "#ef4444";
                      backgroundColor = "#fef2f2";
                      icon = <DeleteOutlined style={{ color: "#ef4444" }} />;
                      break;
                    case "update":
                      borderColor = "#3b82f6";
                      backgroundColor = "#eff6ff";
                      icon = <EditOutlined style={{ color: "#3b82f6" }} />;
                      break;
                    case "tva_override":
                      borderColor = "#6366f1";
                      backgroundColor = "#eef2ff";
                      icon = <RiseOutlined style={{ color: "#6366f1" }} />;
                      break;
                    default:
                      break;
                  }

                  const subtitle = getHistoryCardSubtitle(event);

                  return (
                    <div key={event.id} style={{ padding: "0 8px" }}>
                      <div style={{ 
                        height: "100%", 
                        borderRadius: 12, 
                        border: `1px solid ${borderColor}`,
                        background: backgroundColor,
                        padding: 16,
                        minHeight: 140,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e2e8f0" }}>
                            {icon}
                          </div>
                          <div>
                            <Text strong style={{ display: "block" }}>{event.title}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>{subtitle}</Text>
                          </div>
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>{getHistoryChangeSummary(event)}</Text>
                      </div>
                    </div>
                  );
                })}
              </Carousel>
              {/* Pastilles de pagination custom sous le carousel */}
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                {Array.from({
                  length: Math.max(
                    1,
                    Math.ceil(recentHistoryEvents.length / 3)
                  ),
                }).map((_, index) => {
                  const isActive = index === historyCurrentSlide;
                  return (
                    <div
                      key={index}
                      style={{
                        height: 4,
                        borderRadius: 999,
                        backgroundColor: "#0f172a",
                        opacity: isActive ? 1 : 0.2,
                        width: isActive ? 32 : 8,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer"
                      }}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );

  // Fonction de rendu principal du composant Finance
  return (
    <div className="finance-container">
      {isLoading ? (
        <div style={{ textAlign: "center", padding: "100px" }}>
          <Spin size="large" tip="Chargement des données financières..." />
        </div>
      ) : loadError ? (
        <Alert
          message="Erreur de chargement"
          description={loadError}
          type="error"
          showIcon
          className="neo-card"
        />
      ) : (
        <>
          <div className="finance-header">
            <Typography.Title level={2}>Trésorerie</Typography.Title>
            <Typography.Text>
              Graphique du suivi de trésorerie
            </Typography.Text>
          </div>

          {renderCashFlowSection()}

          <div className="finance-header mt-12">
            <Typography.Title level={2}>Finance 360</Typography.Title>
            <Typography.Text>
              Vision complète de la performance financière, trésorerie, risques et efficacité commerciale.
            </Typography.Text>
          </div>

          <Finance360 />

          <Modal
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <NodeIndexOutlined />
                <span>Synchronisation</span>
              </div>
            }
            open={syncModalVisible}
            onCancel={() => setSyncModalVisible(false)}
            footer={null}
            width={1000}
            style={{ top: 20 }}
            className="neo-modal"
            bodyStyle={{ maxHeight: "85vh", overflowY: "auto", padding: "24px" }}
          >
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              {syncViewMode === "list" ? (
                <div className="neo-card" style={{ padding: "24px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                    <Text strong style={{ fontSize: 18 }}>Synchronisations configurées</Text>
                    <Button type="primary" onClick={startCreateSyncRule}>
                      Nouvelle synchronisation
                    </Button>
                  </div>

                  <Table
                    rowKey={(r: any) => String(r?._id || Math.random())}
                    columns={syncRulesColumns as any}
                    dataSource={syncRules}
                    pagination={{ pageSize: 8 }}
                    loading={syncRulesLoading}
                    className="neo-table"
                  />
                </div>
              ) : (
                <div className="neo-card" style={{ padding: "24px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                    <Text strong style={{ fontSize: 18 }}>{syncSelectedRuleId ? "Modifier la synchronisation" : "Créer une synchronisation"}</Text>
                    <Space>
                      <Button onClick={() => setSyncViewMode("list")}>Annuler</Button>
                      <Button
                        type="primary"
                        loading={syncSaveLoading}
                        onClick={saveRuleFromConfigurator}
                      >
                        Enregistrer
                      </Button>
                    </Space>
                  </div>

                    <Divider style={{ margin: "8px 0" }} />

                    <Card size="small" title="Source & mapping" style={{ background: "#ffffff" }}>
                      <Row gutter={[12, 12]}>
                        <Col xs={24} md={12}>
                          <Text strong>Source (modèle)</Text>
                          <div style={{ marginTop: 6 }}>
                            <Select
                              style={{ width: "100%" }}
                              loading={syncMetadataLoading}
                              value={syncMetadataSelectedModel || undefined}
                              onChange={(v) => {
                                setSyncMetadataSelectedModel(String(v));
                                setSyncMetadataSelectedAmountPath(null);
                                setSyncConfigDueDateBasePath("createdAt");
                              }}
                              options={syncMetadataModels.map((m) => ({
                                label: m.modelName,
                                value: m.modelName,
                              }))}
                            />
                          </div>
                        </Col>
                        <Col xs={24} md={12}>
                          <Text strong>Montant (Number)</Text>
                          <div style={{ marginTop: 6 }}>
                            <Select
                              style={{ width: "100%" }}
                              loading={syncMetadataLoading}
                              value={syncMetadataSelectedAmountPath || undefined}
                              placeholder="Sélectionne un champ Number"
                              onChange={(v) => setSyncMetadataSelectedAmountPath(String(v))}
                              options={(selectedMetadataModel?.fields?.numberFields || []).map((p) => ({
                                label: p,
                                value: p,
                              }))}
                            />
                          </div>
                        </Col>
                        <Col xs={24}>
                          <Text strong>Montant (fallbacks)</Text>
                          <div style={{ marginTop: 6 }}>
                            <Space direction="vertical" style={{ width: "100%" }} size={6}>
                              {(syncConfigAmountFallbackPaths.length ? syncConfigAmountFallbackPaths : []).map(
                                (path, idx) => (
                                  <Space key={`${idx}-${path}`} wrap style={{ width: "100%" }}>
                                    <Select
                                      style={{ flex: 1, minWidth: 220 }}
                                      loading={syncMetadataLoading}
                                      value={path}
                                      onChange={(v) => {
                                        const next = [...syncConfigAmountFallbackPaths];
                                        next[idx] = String(v);
                                        setSyncConfigAmountFallbackPaths(next);
                                      }}
                                      options={(selectedMetadataModel?.fields?.numberFields || []).map((p) => ({
                                        label: p,
                                        value: p,
                                      }))}
                                      placeholder="Champ montant (Number)"
                                    />
                                    <Button
                                      onClick={() => {
                                        const next = [...syncConfigAmountFallbackPaths];
                                        next.splice(idx, 1);
                                        setSyncConfigAmountFallbackPaths(next);
                                      }}
                                    >
                                      Retirer
                                    </Button>
                                  </Space>
                                )
                              )}
                              <Button
                                onClick={() => {
                                  const next = [...syncConfigAmountFallbackPaths];
                                  next.push(String(syncMetadataSelectedAmountPath || ""));
                                  setSyncConfigAmountFallbackPaths(next.filter(Boolean));
                                }}
                                disabled={!syncMetadataSelectedAmountPath}
                              >
                                Ajouter un fallback
                              </Button>
                            </Space>
                          </div>
                        </Col>
                        <Col xs={24} md={12}>
                          <Text strong>Catégorie</Text>
                          <div style={{ marginTop: 6 }}>
                            <Select
                              style={{ width: "100%" }}
                              value={syncConfigCategory}
                              onChange={(v) => setSyncConfigCategory(String(v))}
                              options={[
                                { label: "Dépenses récurrentes", value: "depense_recurrente" },
                                { label: "Dépenses exceptionnelles", value: "depense_exceptionnelle" },
                                { label: "Dépenses ventes", value: "depense_vente" },
                                { label: "Revenus ventes", value: "revenu_vente" },
                                { label: "Revenus exceptionnels", value: "revenu_exceptionnel" },
                              ]}
                            />
                          </div>
                        </Col>
                        <Col xs={24} md={12}>
                          <Text strong>Sens</Text>
                          <div style={{ marginTop: 6 }}>
                            <Radio.Group
                              value={syncConfigIsRevenue ? "revenue" : "expense"}
                              onChange={(e) => setSyncConfigIsRevenue(e.target.value === "revenue")}
                              options={[
                                { label: "Dépense", value: "expense" },
                                { label: "Revenu", value: "revenue" },
                              ]}
                              optionType="button"
                              buttonStyle="solid"
                            />
                          </div>
                        </Col>
                        <Col xs={24}>
                          <Text strong>Libellé (template)</Text>
                          <div style={{ marginTop: 6 }}>
                            <Input
                              value={syncConfigTitleTemplate}
                              onChange={(e) => setSyncConfigTitleTemplate(e.target.value)}
                              placeholder="Ex: Bon de commande {reference}"
                            />
                          </div>
                        </Col>
                      </Row>
                    </Card>

                    <Card size="small" title="TVA" style={{ background: "#ffffff" }}>
                      <Row gutter={[12, 12]} align="middle">
                        <Col xs={24} md={12}>
                          <Text strong>Mode</Text>
                          <div style={{ marginTop: 6 }}>
                            <Radio.Group
                              value={syncConfigTvaMode}
                              onChange={(e) => setSyncConfigTvaMode(e.target.value)}
                              options={[
                                { label: "Aucune", value: "none" },
                                { label: "Fixe", value: "fixed_rate" },
                                { label: "Depuis champ", value: "rate_from_field" },
                              ]}
                              optionType="button"
                              buttonStyle="solid"
                            />
                          </div>
                        </Col>
                        <Col xs={24} md={12}>
                          {syncConfigTvaMode === "fixed_rate" ? (
                            <>
                              <Text strong>Taux</Text>
                              <div style={{ marginTop: 6 }}>
                                <Select
                                  style={{ width: "100%" }}
                                  value={syncConfigTauxTVA}
                                  onChange={(v) => setSyncConfigTauxTVA(Number(v) as any)}
                                  options={[0, 5.5, 10, 20].map((r) => ({ label: `${r}%`, value: r }))}
                                />
                              </div>
                            </>
                          ) : syncConfigTvaMode === "rate_from_field" ? (
                            <>
                              <Text strong>Champ TVA (Number)</Text>
                              <div style={{ marginTop: 6 }}>
                                <Select
                                  style={{ width: "100%" }}
                                  loading={syncMetadataLoading}
                                  value={syncConfigTvaRatePath || undefined}
                                  placeholder="Champ TVA (Number)"
                                  onChange={(v) => setSyncConfigTvaRatePath(String(v))}
                                  options={(selectedMetadataModel?.fields?.numberFields || []).map((p) => ({
                                    label: p,
                                    value: p,
                                  }))}
                                />
                              </div>
                            </>
                          ) : null}
                        </Col>
                      </Row>
                    </Card>

                    <Card size="small" title="Échéance" style={{ background: "#ffffff" }}>
                      <Row gutter={[12, 12]}>
                        <Col xs={24} md={12}>
                          <Text strong>Base date (avec backups)</Text>
                          <div style={{ marginTop: 6 }}>
                            <Space direction="vertical" style={{ width: "100%" }} size={6}>
                              {(syncConfigDueDateBasePaths.length ? syncConfigDueDateBasePaths : [syncConfigDueDateBasePath]).map(
                                (path, idx) => (
                                  <Space key={`${idx}-${path}`} wrap style={{ width: "100%" }}>
                                    <Select
                                      style={{ flex: 1, minWidth: 220 }}
                                      loading={syncMetadataLoading}
                                      value={path}
                                      onChange={(v) => {
                                        const next = [...(syncConfigDueDateBasePaths.length ? syncConfigDueDateBasePaths : [syncConfigDueDateBasePath])];
                                        next[idx] = String(v);
                                        setSyncConfigDueDateBasePaths(next);
                                        if (idx === 0) setSyncConfigDueDateBasePath(String(v));
                                      }}
                                      options={(selectedMetadataModel?.fields?.dateFields || []).map((p) => ({
                                        label: p,
                                        value: p,
                                      }))}
                                      placeholder="Champ date"
                                    />
                                    {idx > 0 && (
                                      <Button
                                        onClick={() => {
                                          const next = [...syncConfigDueDateBasePaths];
                                          next.splice(idx, 1);
                                          setSyncConfigDueDateBasePaths(next.length ? next : [syncConfigDueDateBasePath]);
                                        }}
                                      >
                                        Retirer
                                      </Button>
                                    )}
                                  </Space>
                                )
                              )}
                              <Button
                                onClick={() => {
                                  const next = [...syncConfigDueDateBasePaths];
                                  next.push(syncConfigDueDateBasePath || "createdAt");
                                  setSyncConfigDueDateBasePaths(next);
                                }}
                              >
                                Ajouter un backup
                              </Button>
                            </Space>
                          </div>
                        </Col>

                        <Col xs={24} md={12}>
                          <Row gutter={[12, 12]}>
                            <Col xs={24}>
                              <Text strong>Transform</Text>
                              <div style={{ marginTop: 6 }}>
                                <Select
                                  style={{ width: "100%" }}
                                  value={syncConfigDueDateTransform}
                                  onChange={(v) => setSyncConfigDueDateTransform(v)}
                                  options={[
                                    { label: "Aucune", value: "none" },
                                    { label: "Fin de mois", value: "end_of_month" },
                                  ]}
                                />
                              </div>
                            </Col>
                            <Col xs={24}>
                              <Text strong>+ jours</Text>
                              <div style={{ marginTop: 6 }}>
                                <Space wrap style={{ width: "100%" }}>
                                  <Radio.Group
                                    value={syncConfigOffsetMode}
                                    onChange={(e) => setSyncConfigOffsetMode(e.target.value)}
                                    options={[
                                      { label: "Fixe", value: "constant_days" },
                                      { label: "Depuis champ", value: "field_days" },
                                    ]}
                                    optionType="button"
                                    buttonStyle="solid"
                                  />
                                  {syncConfigOffsetMode === "constant_days" ? (
                                    <InputNumber
                                      value={syncConfigDueDateOffsetDays}
                                      onChange={(v) => setSyncConfigDueDateOffsetDays(Number(v || 0))}
                                    />
                                  ) : (
                                    <Select
                                      style={{ minWidth: 220 }}
                                      loading={syncMetadataLoading}
                                      value={syncConfigOffsetDaysPath || undefined}
                                      placeholder="Champ délais (Number)"
                                      onChange={(v) => setSyncConfigOffsetDaysPath(String(v))}
                                      options={(selectedMetadataModel?.fields?.numberFields || []).map((p) => ({
                                        label: p,
                                        value: p,
                                      }))}
                                    />
                                  )}
                                </Space>
                              </div>
                            </Col>
                            <Col xs={24}>
                              <Button
                                type="primary"
                                loading={syncPreviewLoading}
                                onClick={previewExampleFromConfig}
                                disabled={!syncMetadataSelectedModel || !syncMetadataSelectedAmountPath}
                              >
                                Preview exemple
                              </Button>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Card>

                    <Table
                      rowKey={(r: any) => String(r?.source?.hash || Math.random())}
                      columns={syncPreviewColumns as any}
                      dataSource={syncPreview}
                      pagination={false}
                      loading={syncPreviewLoading}
                    />
                  </div>
                )}
              </Space>
            </Modal>

          {/* Modal de détail pour un élément */}
          <Modal
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ 
                  width: 32, height: 32, background: "#f8fafc", borderRadius: 8, 
                  display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e2e8f0" 
                }}>
                  <InfoCircleOutlined style={{ color: "#0f172a" }} />
                </div>
                <span>Détails de l'opération</span>
              </div>
            }
            open={modalVisible}
            onCancel={handleModalClose}
            className="neo-modal"
            width={500}
            footer={[
              <Button key="close" onClick={handleModalClose} style={{ height: 40, borderRadius: 8, fontWeight: 600 }}>
                Fermer
              </Button>,
            ]}
          >
            {selectedItem && (
              <div style={{ padding: "8px 0" }}>
                <div className="neo-card" style={{ padding: 20, background: "#f8fafc", border: "1px solid #f1f5f9", marginBottom: 20 }}>
                  <Text type="secondary" style={{ fontSize: 12, textTransform: "uppercase", fontWeight: 700, display: "block", marginBottom: 4 }}>Libellé</Text>
                  <Text strong style={{ fontSize: 18, color: "#0f172a" }}>{selectedItem.libelle}</Text>
                </div>

                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700, display: "block" }}>Catégorie</Text>
                    <Tag style={{ marginTop: 4, borderRadius: 6, fontWeight: 600 }}>
                      {selectedItem.categorie ? selectedItem.categorie.replace("_", " ").toUpperCase() : "NON SPÉCIFIÉE"}
                    </Tag>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700, display: "block" }}>Type</Text>
                    <Text strong style={{ display: "block", marginTop: 4 }}>
                      {selectedItem.type === CashFlowItemType.RECURRENT ? "Récurrent" : "Ponctuel"}
                    </Text>
                  </Col>
                  
                  <Col span={24}>
                    <Divider style={{ margin: "8px 0" }} />
                  </Col>

                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700, display: "block" }}>Montant HT</Text>
                    <Text strong style={{ fontSize: 15 }}>{formatCurrency(selectedItem.montantHT)}</Text>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700, display: "block" }}>TVA ({selectedItem.tauxTVA}%)</Text>
                    <Text strong style={{ fontSize: 15 }}>{formatCurrency(selectedItem.tva)}</Text>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700, display: "block" }}>Montant TTC</Text>
                    <Text strong style={{ fontSize: 16, color: selectedItem.isRevenue ? "#10b981" : "#ef4444" }}>
                      {formatCurrency(selectedItem.montantTTC)}
                    </Text>
                  </Col>

                  <Col span={24}>
                    <Divider style={{ margin: "8px 0" }} />
                  </Col>

                  <Col span={24}>
                    <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700, display: "block" }}>Échéance / Prélèvement</Text>
                    <Text strong style={{ fontSize: 15 }}>{formatEcheance(selectedItem)}</Text>
                  </Col>
                </Row>
              </div>
            )}
          </Modal>

          {/* Modal pour les détails de catégorie */}
          <Modal
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ 
                  width: 32, height: 32, background: "#f8fafc", borderRadius: 8, 
                  display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e2e8f0" 
                }}>
                  <AreaChartOutlined style={{ color: "#0f172a" }} />
                </div>
                <span>
                  {categoryModalData?.category
                    .replace("_", " ")
                    .replace(/^\w/, (c) => c.toUpperCase())}
                </span>
              </div>
            }
            open={categoryModalVisible}
            onCancel={handleCategoryModalClose}
            width={1000}
            className="neo-modal"
            footer={[
              <Button key="close" onClick={handleCategoryModalClose} style={{ height: 40, borderRadius: 8 }}>
                Fermer
              </Button>,
              <Button
                key="add"
                type="primary"
                icon={categoryModalData?.isRevenue ? <PlusOutlined /> : <MinusOutlined />}
                onClick={handleAddItem}
                style={{ height: 40, borderRadius: 8 }}
              >
                {categoryModalData?.isRevenue ? "Ajouter un revenu" : "Ajouter une dépense"}
              </Button>,
            ]}
          >
            {categoryModalData && (() => {
              const today = dayjs().startOf("day");
              const tenDaysAgo = today.subtract(10, "day");

              const isRecentOverdue = (item: CashFlowItem) => {
                if (
                  item.type === CashFlowItemType.PONCTUEL &&
                  item.echeance
                ) {
                  const echeance = dayjs(item.echeance);
                  return (
                    echeance.isBefore(today, "day") &&
                    !echeance.isBefore(tenDaysAgo, "day")
                  );
                }
                return false;
              };

              const sortByMonthTypeDateLabel = (
                a: CashFlowItem,
                b: CashFlowItem
              ) => {
                const monthA = getItemMonthKey(a);
                const monthB = getItemMonthKey(b);

                if (monthA !== monthB) {
                  return monthA.localeCompare(monthB);
                }

                if (a.type !== b.type) {
                  if (a.type === CashFlowItemType.RECURRENT) return -1;
                  if (b.type === CashFlowItemType.RECURRENT) return 1;
                }

                const dateA = getItemReferenceDate(a);
                const dateB = getItemReferenceDate(b);

                if (dateA && dateB) {
                  const diff = dateA.diff(dateB);
                  if (diff !== 0) return diff;
                }

                return (a.libelle || "").localeCompare(b.libelle || "");
              };

              const overdueItems = categoryModalData.items
                .filter(isRecentOverdue)
                .sort(sortByMonthTypeDateLabel);
              const activeItems = categoryModalData.items
                .filter((item) => !isRecentOverdue(item))
                .sort(sortByMonthTypeDateLabel);

              const renderEcheanceCell = (record: CashFlowItem) => {
                let label = "Date non spécifiée";
                let isOverdueRecent = false;

                if (
                  record.type === CashFlowItemType.RECURRENT &&
                  record.jourDuMois
                ) {
                  label = `Prélèvement le ${record.jourDuMois}`;
                } else if (record.echeance) {
                  const echeance = dayjs(record.echeance);
                  label = echeance.format("DD/MM/YYYY");
                  if (
                    echeance.isBefore(today, "day") &&
                    !echeance.isBefore(tenDaysAgo, "day")
                  ) {
                    isOverdueRecent = true;
                  }
                } else if (record.dateDebut) {
                  label = dayjs(record.dateDebut).format("DD/MM/YYYY");
                }

                const style = isOverdueRecent
                  ? { color: "#cf1322", fontWeight: 600 }
                  : undefined;

                return (
                  <span style={style}>
                    {label}
                    {isOverdueRecent ? " (échu)" : ""}
                  </span>
                );
              };

              const groupItemsByMonthForDisplay = (
                items: CashFlowItem[]
              ): { key: string; label: string; items: CashFlowItem[] }[] => {
                const groups: {
                  key: string;
                  label: string;
                  items: CashFlowItem[];
                }[] = [];

                items.forEach((item) => {
                  const key = getItemMonthKey(item);
                  const label = getItemMonthLabel(item);
                  const last = groups[groups.length - 1];

                  if (!last || last.key !== key) {
                    groups.push({ key, label, items: [item] });
                  } else {
                    last.items.push(item);
                  }
                });

                return groups;
              };

              const activeColumns = [
                {
                  title: "Inclure",
                  dataIndex: "id",
                  key: "include",
                  width: 80,
                  render: (id: string | undefined) => (
                    <Checkbox
                      checked={id ? selectedItems[id] !== false : true}
                      onChange={(e: CheckboxChangeEvent) =>
                        handleItemCheckChange(id, e.target.checked)
                      }
                    />
                  ),
                },
                {
                  title: "Libellé",
                  dataIndex: "libelle",
                  key: "libelle",
                  render: (_: any, record: CashFlowItem) => {
                    const isSync = !!(record as any)?.sync || !!(record as any)?.source;
                    return (
                      <Space size={6} wrap>
                        {isSync ? <Tag color="blue">Sync</Tag> : null}
                        <span>{String((record as any)?.libelle || "")}</span>
                      </Space>
                    );
                  },
                },
                {
                  title: "Type",
                  dataIndex: "type",
                  key: "type",
                  width: 120,
                },
                {
                  title: "Échéance",
                  dataIndex: "echeance",
                  key: "echeance",
                  width: 160,
                  render: (_: any, record: CashFlowItem) =>
                    renderEcheanceCell(record),
                },
                {
                  title: "Montant TTC",
                  dataIndex: "montantTTC",
                  key: "montantTTC",
                  width: 120,
                  render: (amount: number) => (
                    <span
                      style={{
                        color: categoryModalData.isRevenue
                          ? "#3f8600"
                          : "#cf1322",
                      }}
                    >
                      {categoryModalData.isRevenue ? "" : "-"}
                      {amount.toLocaleString("fr-FR")} €
                    </span>
                  ),
                },
                {
                  title: "Actions",
                  key: "actions",
                  width: 100,
                  render: (_: any, record: CashFlowItem) => (
                    <Space>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEditClick(record)}
                      />
                      {record.type === CashFlowItemType.RECURRENT && (
                        <Tooltip
                          overlayInnerStyle={{ backgroundColor: "white" }}
                          title={
                            <div style={{ padding: "8px 0" }}>
                              <div style={{ marginBottom: "8px" }}>
                                <Text strong>Date de fin:</Text>
                              </div>
                              <DatePicker
                                value={
                                  archivingItem?._id === record._id
                                    ? archiveDate
                                    : null
                                }
                                onChange={(date) => setArchiveDate(date)}
                                style={{
                                  width: "100%",
                                  marginBottom: "8px",
                                }}
                                disabledDate={(current) =>
                                  current && current < dayjs().startOf("day")
                                }
                                placeholder="Sélectionner une date de fin"
                              />
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  marginTop: "8px",
                                }}
                              >
                                <Button
                                  size="small"
                                  onClick={handleArchiveTooltipClose}
                                >
                                  Annuler
                                </Button>
                                <Button
                                  size="small"
                                  type="primary"
                                  disabled={!archiveDate}
                                  onClick={() =>
                                    archiveDate &&
                                    handleArchiveSubmit(record, archiveDate)
                                  }
                                >
                                  Valider
                                </Button>
                              </div>
                            </div>
                          }
                          open={
                            archivingItem?._id === record._id &&
                            archiveTooltipVisible
                          }
                          trigger="click"
                          placement="topRight"
                          onOpenChange={(visible) => {
                            if (visible) {
                              handleArchiveClick(record);
                            } else {
                              handleArchiveTooltipClose();
                            }
                          }}
                        >
                          <Button
                            type="text"
                            icon={<InboxOutlined />}
                            title="Archiver (définir une date de fin)"
                          />
                        </Tooltip>
                      )}
                      <Popconfirm
                        title="Êtes-vous sûr de vouloir supprimer cet élément ?"
                        onConfirm={() =>
                          record._id && handleConfirmDelete(record._id)
                        }
                        okText="Oui"
                        cancelText="Non"
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    </Space>
                  ),
                },
              ];

              const overdueColumns = [
                {
                  title: "Échu",
                  dataIndex: "id",
                  key: "overdue",
                  width: 80,
                  render: () => <Checkbox checked disabled />,
                },
                {
                  title: "Libellé",
                  dataIndex: "libelle",
                  key: "libelle",
                },
                {
                  title: "Type",
                  dataIndex: "type",
                  key: "type",
                  width: 120,
                },
                {
                  title: "Échéance",
                  dataIndex: "echeance",
                  key: "echeance",
                  width: 220,
                  render: (_: any, record: CashFlowItem) => (
                    <Space direction="vertical" size={2}>
                      {renderEcheanceCell(record)}
                      <DatePicker
                        size="small"
                        format="DD/MM/YYYY"
                        value={
                          record.echeance ? dayjs(record.echeance) : null
                        }
                        onChange={(date) =>
                          handleOverdueEcheanceChange(record, date)
                        }
                      />
                    </Space>
                  ),
                },
                {
                  title: "Montant TTC",
                  dataIndex: "montantTTC",
                  key: "montantTTC",
                  width: 120,
                  render: (amount: number) => (
                    <span
                      style={{
                        color: categoryModalData.isRevenue
                          ? "#3f8600"
                          : "#cf1322",
                      }}
                    >
                      {categoryModalData.isRevenue ? "" : "-"}
                      {amount.toLocaleString("fr-FR")} €
                    </span>
                  ),
                },
              ];

              const totalActive = activeItems
                .filter((item) => item.id && selectedItems[item.id])
                .reduce((sum, item) => sum + item.montantTTC, 0);

              const recurrentActiveItems = activeItems.filter(
                (item) => item.type === CashFlowItemType.RECURRENT
              );
              const punctualActiveItems = activeItems.filter(
                (item) => item.type !== CashFlowItemType.RECURRENT
              );

              const recurrentGroups =
                groupItemsByMonthForDisplay(recurrentActiveItems);
              const punctualGroups =
                groupItemsByMonthForDisplay(punctualActiveItems);
              const overdueGroups = groupItemsByMonthForDisplay(overdueItems);

              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: 520,
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      overflowY: "auto",
                      paddingRight: 8,
                    }}
                  >
                    {recurrentGroups.length > 0 && (
                      <>
                        <Typography.Title
                          level={5}
                          style={{ marginBottom: 8 }}
                        >
                          Opérations récurrentes
                        </Typography.Title>

                        {recurrentGroups.map((group) => (
                          <div
                            key={group.key}
                            style={{ marginBottom: 16 }}
                          >
                            <Table
                              dataSource={group.items}
                              rowKey="id"
                              pagination={false}
                              size="small"
                              columns={activeColumns}
                            />
                          </div>
                        ))}

                        <Divider />
                      </>
                    )}

                    {punctualGroups.length > 0 && (
                      <>
                        <Typography.Title
                          level={5}
                          style={{ marginBottom: 8 }}
                        >
                          Tickets à venir / actifs
                        </Typography.Title>

                        {punctualGroups.map((group) => (
                          <div
                            key={group.key}
                            style={{ marginBottom: 16 }}
                          >
                            <Text
                              strong
                              style={{
                                display: "block",
                                marginBottom: 4,
                              }}
                            >
                              {group.label}
                            </Text>
                            <Table
                              dataSource={group.items}
                              rowKey="id"
                              pagination={false}
                              size="small"
                              columns={activeColumns}
                            />
                          </div>
                        ))}
                      </>
                    )}

                    {overdueItems.length > 0 && (
                      <>
                        <Divider />
                        <Typography.Title
                          level={5}
                          style={{ marginBottom: 8 }}
                        >
                          Tickets échus (10 derniers jours)
                        </Typography.Title>
                        {overdueGroups.map((group) => (
                          <div key={group.key} style={{ marginBottom: 16 }}>
                            <Text
                              strong
                              style={{ display: "block", marginBottom: 4 }}
                            >
                              {group.label}
                            </Text>
                            <Table
                              dataSource={group.items}
                              rowKey="id"
                              pagination={false}
                              size="small"
                              columns={overdueColumns}
                              className="neo-table"
                            />
                          </div>
                        ))}
                      </>
                    )}

                    {(categorySyncProposalsLoading || categorySyncProposals.length > 0) && (
                      <>
                        <Divider style={{ margin: "16px 0" }} />

                        <Typography.Title level={5} style={{ marginBottom: 8 }}>
                          Propositions de synchronisation
                        </Typography.Title>

                        {categorySyncProposalsLoading ? (
                          <Spin />
                        ) : (
                          (() => {
                            const sorted = [...categorySyncProposals].sort((a, b) =>
                              dayjs(a.echeance).valueOf() - dayjs(b.echeance).valueOf()
                            );

                            return (
                              <div style={{ position: "relative" }}>
                                <style>
                                  {`.cf-sync-proposals-row::-webkit-scrollbar{display:none;}`}
                                </style>
                                <div
                                  ref={categorySyncCarouselRef}
                                  style={{
                                    overflow: "hidden",
                                    padding: "0 0px",
                                  }}
                                >
                                  <div
                                    className="cf-sync-proposals-row"
                                    style={{
                                      display: "flex",
                                      gap: 12,
                                      overflowX: "auto",
                                      scrollSnapType: "x mandatory",
                                      paddingBottom: 8,
                                      scrollbarWidth: "none",
                                      msOverflowStyle: "none",
                                    }}
                                  >
                                    {sorted.map((p) => {
                                      const h = p?.source?.hash;
                                      const disabled = !!p.alreadyExists;
                                      const isSaving = !!h && categorySyncSavingHash === h;
                                      const bg = disabled ? "#fafafa" : "#ffffff";

                                      const isNewToday = !!p.sourceDate && dayjs(p.sourceDate).isSame(dayjs(), "day");

                                      return (
                                        <div
                                          key={h || Math.random()}
                                          style={{
                                            width: 320,
                                            flex: "0 0 auto",
                                            scrollSnapAlign: "start",
                                          }}
                                        >
                                          <Card
                                            size="small"
                                            style={{
                                              border: "1px solid #000",
                                              borderRadius: 8,
                                              background: bg,
                                              height: 120,
                                            }}
                                            bodyStyle={{
                                              padding: 12,
                                              height: 120,
                                              display: "flex",
                                              flexDirection: "column",
                                              justifyContent: "space-between",
                                            }}
                                          >
                                            <div>
                                              <Text strong style={{ display: "block" }}>
                                                {p.libelle}
                                              </Text>
                                              <Text type="secondary" style={{ fontSize: 12 }}>
                                                Échéance: {p.echeance ? dayjs(p.echeance).format("DD/MM/YYYY") : "-"}
                                              </Text>
                                              <br />
                                              <Text type="secondary" style={{ fontSize: 12 }}>
                                                TTC: {Number(p.montantTTC || 0).toLocaleString("fr-FR")} €
                                                {p.tauxTVA ? ` • TVA ${Number(p.tauxTVA).toLocaleString("fr-FR")}%` : ""}
                                              </Text>
                                            </div>

                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                              <Text type="secondary" style={{ fontSize: 12 }}>
                                                {disabled ? (
                                                  "Déjà créé"
                                                ) : isNewToday ? (
                                                  <Tag color="magenta">Nouveau</Tag>
                                                ) : (
                                                  ""
                                                )}
                                              </Text>
                                              <Button
                                                size="small"
                                                type="primary"
                                                loading={isSaving}
                                                disabled={disabled || !h}
                                                onClick={() => applySingleCategorySyncProposal(p)}
                                              >
                                                Sauvegarder
                                              </Button>
                                            </div>
                                          </Card>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            );
                          })()
                        )}
                      </>
                    )}
                  </div>

                  <Divider style={{ margin: "12px 0" }} />

                  <Row justify="end">
                    <Col>
                      <Statistic
                        title="Total (tickets actifs sélectionnés)"
                        value={totalActive}
                        precision={2}
                        suffix="€"
                        valueStyle={{
                          color: categoryModalData.isRevenue
                            ? "#3f8600"
                            : "#cf1322",
                          fontSize: "18px",
                        }}
                      />
                    </Col>
                  </Row>

                </div>
              );
            })()}
          </Modal>

          {/* Modale TVA avec tableaux mensuels */}
          <Modal
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ 
                  width: 32, height: 32, background: "#f8fafc", borderRadius: 8, 
                  display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e2e8f0" 
                }}>
                  <RiseOutlined style={{ color: "#0f172a" }} />
                </div>
                <span>Synthèse TVA</span>
              </div>
            }
            open={tvaModalVisible}
            onCancel={handleTVAModalClose}
            width={1000}
            footer={null}
            className="neo-modal"
            bodyStyle={{ maxHeight: "80vh", overflowY: "auto", padding: "24px" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
              {tvaData.map((monthData) => {
                const tvaDue = monthData.revenus.reduce((sum, item) => sum + item.tva, 0);
                const tvaDeductible = monthData.depenses.reduce((sum, item) => sum + item.tva, 0);
                const effectiveSolde = monthData.overrideSoldeTVA ?? monthData.soldeTVA;
                const baseMonth = dayjs(monthData.monthKey);
                const dateEcheance = effectiveSolde > 0
                    ? baseMonth.clone().add(1, "month").date(27).format("DD/MM/YYYY")
                    : baseMonth.clone().add(2, "month").date(5).format("DD/MM/YYYY");

                return (
                  <div key={monthData.mois} className="neo-card" style={{ padding: 0 }}>
                    <div style={{ 
                      padding: "16px 24px", 
                      borderBottom: "1px solid #f1f5f9", 
                      background: "#f8fafc",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                      <Text strong style={{ fontSize: 18, color: "#0f172a" }}>{monthData.mois}</Text>
                      <Tag color={effectiveSolde <= 0 ? "success" : "error"} style={{ borderRadius: 6, fontWeight: 600 }}>
                        {effectiveSolde <= 0 ? "CRÉDIT" : "À PAYER"}
                      </Tag>
                    </div>

                    <div style={{ padding: 24 }}>
                      <Row gutter={24} style={{ marginBottom: 24 }}>
                        <Col span={12}>
                          <div style={{ 
                            background: effectiveSolde <= 0 ? "#f0fdf4" : "#fef2f2", 
                            padding: 20, 
                            borderRadius: 12,
                            border: `1px solid ${effectiveSolde <= 0 ? "#bbf7d0" : "#fecaca"}`,
                            textAlign: "center"
                          }}>
                            <Text type="secondary" style={{ fontSize: 12, textTransform: "uppercase", fontWeight: 700, display: "block", marginBottom: 8 }}>
                              Solde TVA estimé
                            </Text>
                            <Title level={3} style={{ margin: 0, color: effectiveSolde <= 0 ? "#15803d" : "#dc2626" }}>
                              {effectiveSolde <= 0 ? "+" : "-"}{Math.abs(effectiveSolde).toLocaleString("fr-FR")} €
                            </Title>
                            <Text style={{ fontSize: 12, color: effectiveSolde <= 0 ? "#166534" : "#991b1b", display: "block", marginTop: 4 }}>
                              Échéance : {dateEcheance}
                            </Text>
                          </div>
                        </Col>
                        <Col span={12}>
                          <div style={{ padding: "0 12px" }}>
                            <Text strong style={{ display: "block", marginBottom: 8 }}>Ajuster le montant de TVA :</Text>
                            <InputNumber
                              value={effectiveSolde}
                              onChange={(value) => handleTVAOverrideChange(monthData.mois, value)}
                              formatter={(value) => `${value !== undefined && value !== null ? Number(value).toLocaleString("fr-FR") : 0} €`}
                              parser={(value) => value ? parseFloat(value.replace(/\s/g, "").replace("€", "").replace(/,/g, ".")) : 0}
                              style={{ width: "100%", height: 40, borderRadius: 8 }}
                            />
                            <Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: "block" }}>
                              Modifiez ce montant si vous avez déjà déclaré ou payé une partie.
                            </Text>
                          </div>
                        </Col>
                      </Row>

                      <Collapse ghost expandIconPosition="end" className="neo-collapse">
                        <Collapse.Panel
                          header={<Text strong style={{ color: "#64748b" }}>VOIR LE DÉTAIL DES OPÉRATIONS</Text>}
                          key="1"
                        >
                          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                            <div>
                              <Text strong style={{ color: "#dc2626", display: "block", marginBottom: 12 }}>REVENUS (TVA DUE)</Text>
                              <Table
                                dataSource={monthData.revenus}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                className="neo-table"
                                columns={[
                                  { title: "Libellé", dataIndex: "libelle", key: "libelle" },
                                  { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                  { title: "TTC", dataIndex: "montantTTC", key: "montantTTC", width: 100, align: "right", render: (v) => `${v.toLocaleString("fr-FR")} €` },
                                  { title: "TVA", dataIndex: "tva", key: "tva", width: 100, align: "right", render: (v) => `${v.toLocaleString("fr-FR")} €` },
                                  { title: "Taux", dataIndex: "tauxTVA", key: "tauxTVA", width: 80, align: "right", render: (v) => `${v}%` },
                                ]}
                                summary={() => (
                                  <Table.Summary.Row style={{ background: "#fff1f0" }}>
                                    <Table.Summary.Cell index={0} colSpan={3}>
                                      <Text strong>TOTAL TVA COLLECTÉE</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right">
                                      <Text strong>{tvaDue.toLocaleString("fr-FR")} €</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} />
                                  </Table.Summary.Row>
                                )}
                              />
                            </div>

                            <div>
                              <Text strong style={{ color: "#15803d", display: "block", marginBottom: 12 }}>DÉPENSES (TVA DÉDUCTIBLE)</Text>
                              <Table
                                dataSource={monthData.depenses}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                className="neo-table"
                                columns={[
                                  { title: "Libellé", dataIndex: "libelle", key: "libelle" },
                                  { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                  { title: "TTC", dataIndex: "montantTTC", key: "montantTTC", width: 100, align: "right", render: (v) => `${v.toLocaleString("fr-FR")} €` },
                                  { title: "TVA", dataIndex: "tva", key: "tva", width: 100, align: "right", render: (v) => `${v.toLocaleString("fr-FR")} €` },
                                  { title: "Taux", dataIndex: "tauxTVA", key: "tauxTVA", width: 80, align: "right", render: (v) => `${v}%` },
                                ]}
                                summary={() => (
                                  <Table.Summary.Row style={{ background: "#f6ffed" }}>
                                    <Table.Summary.Cell index={0} colSpan={3}>
                                      <Text strong>TOTAL TVA DÉDUCTIBLE</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right">
                                      <Text strong>{tvaDeductible.toLocaleString("fr-FR")} €</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} />
                                  </Table.Summary.Row>
                                )}
                              />
                            </div>
                          </div>
                        </Collapse.Panel>
                      </Collapse>
                    </div>
                  </div>
                );
              })}
            </div>
          </Modal>

          <Divider />



          {/* Modal pour la création d'une dépense ou d'un revenu */}
          <Modal
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ 
                  width: 32, height: 32, background: createFormData?.isRevenue ? "#f0fdf4" : "#fef2f2", borderRadius: 8, 
                  display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${createFormData?.isRevenue ? "#bbf7d0" : "#fecaca"}` 
                }}>
                  {createFormData?.isRevenue ? <PlusOutlined style={{ color: "#15803d" }} /> : <MinusOutlined style={{ color: "#dc2626" }} />}
                </div>
                <span>{editingItem ? "Modifier l'opération" : createFormData?.isRevenue ? "Nouveau revenu" : "Nouvelle dépense"}</span>
              </div>
            }
            open={createModalVisible}
            onCancel={handleCreateModalClose}
            width={650}
            className="neo-modal"
            footer={[
              <Button key="cancel" onClick={handleCreateModalClose} disabled={loading} style={{ height: 40, borderRadius: 8 }}>
                Annuler
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={editingItem ? handleEditFormSubmit : handleCreateFormSubmit}
                loading={loading}
                style={{ height: 40, borderRadius: 8 }}
              >
                {editingItem ? "Mettre à jour" : "Valider"}
              </Button>,
            ]}
          >
            {createFormData && (
              <div style={{ padding: "8px 0" }}>
                <Row gutter={[24, 24]}>
                  <Col span={24}>
                    <div style={{ marginBottom: 4 }}>
                      <Text strong style={{ color: "#64748b", fontSize: 12, textTransform: "uppercase" }}>Libellé de l'opération</Text>
                      <Input
                        placeholder="Ex: Loyer, Facture client..."
                        value={createFormData.libelle}
                        onChange={(e) => setCreateFormData({ ...createFormData, libelle: e.target.value })}
                        style={{ width: "100%", height: 40, marginTop: 4 }}
                      />
                    </div>
                  </Col>

                  <Col span={24}>
                    <div style={{ background: "#f8fafc", padding: 16, borderRadius: 12, border: "1px solid #f1f5f9" }}>
                      <Text strong style={{ display: "block", marginBottom: 12, fontSize: 13 }}>Mode de saisie du montant</Text>
                      <Radio.Group
                        value={createFormData.inputMode}
                        onChange={(e) => setCreateFormData({ ...createFormData, inputMode: e.target.value })}
                        style={{ width: "100%" }}
                        buttonStyle="solid"
                      >
                        <Radio.Button value="HT" style={{ width: "50%", textAlign: "center", height: 36, lineHeight: "34px" }}>Montant HT</Radio.Button>
                        <Radio.Button value="TTC" style={{ width: "50%", textAlign: "center", height: 36, lineHeight: "34px" }}>Montant TTC</Radio.Button>
                      </Radio.Group>

                      <div style={{ marginTop: 20 }}>
                        <Row gutter={16} align="bottom">
                          <Col span={14}>
                            <Text strong style={{ fontSize: 12, color: "#64748b" }}>
                              {createFormData.inputMode === "HT" ? "VALEUR HT" : "VALEUR TTC"}
                            </Text>
                            <InputNumber
                              value={createFormData.inputMode === "HT" ? createFormData.montantHT : createFormData.montantTTC}
                              onChange={(value) => {
                                const montant = value || 0;
                                const coefTVA = createFormData.tauxTVA / 100;
                                if (createFormData.inputMode === "HT") {
                                  const newTVA = Number((montant * coefTVA).toFixed(2));
                                  setCreateFormData({ ...createFormData, montantHT: montant, tva: newTVA, montantTTC: Number((montant + newTVA).toFixed(2)) });
                                } else {
                                  const newHT = Number((montant / (1 + coefTVA)).toFixed(2));
                                  setCreateFormData({ ...createFormData, montantTTC: montant, montantHT: newHT, tva: Number((montant - newHT).toFixed(2)) });
                                }
                              }}
                              formatter={(v) => `${v} €`}
                              style={{ width: "100%", height: 40, marginTop: 4, fontWeight: 700 }}
                            />
                          </Col>
                          <Col span={10}>
                            <Text strong style={{ fontSize: 12, color: "#64748b" }}>TAUX TVA</Text>
                            <Select
                              value={createFormData.tauxTVA}
                              onChange={(value) => {
                                const coefTVA = value / 100;
                                if (createFormData.inputMode === "HT") {
                                  const newTVA = Number((createFormData.montantHT * coefTVA).toFixed(2));
                                  setCreateFormData({ ...createFormData, tauxTVA: value, tva: newTVA, montantTTC: Number((createFormData.montantHT + newTVA).toFixed(2)) });
                                } else {
                                  const newHT = Number((createFormData.montantTTC / (1 + coefTVA)).toFixed(2));
                                  setCreateFormData({ ...createFormData, tauxTVA: value, montantHT: newHT, tva: Number((createFormData.montantTTC - newHT).toFixed(2)) });
                                }
                              }}
                              style={{ width: "100%", height: 40, marginTop: 4 }}
                            >
                              <Option value={0}>0%</Option>
                              <Option value={5.5}>5.5%</Option>
                              <Option value={10}>10%</Option>
                              <Option value={20}>20%</Option>
                            </Select>
                          </Col>
                        </Row>
                      </div>

                      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", padding: "12px 0 0", borderTop: "1px dashed #e2e8f0" }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          HT: <strong>{createFormData.montantHT.toLocaleString("fr-FR")} €</strong>
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          TVA ({createFormData.tauxTVA}%): <strong>{createFormData.tva.toLocaleString("fr-FR")} €</strong>
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          TTC: <strong>{createFormData.montantTTC.toLocaleString("fr-FR")} €</strong>
                        </Text>
                      </div>
                    </div>
                  </Col>

                  <Col span={12}>
                    <Text strong style={{ color: "#64748b", fontSize: 12, textTransform: "uppercase" }}>Type d'opération</Text>
                    <Select
                      value={createFormData.type}
                      onChange={(v) => setCreateFormData({ ...createFormData, type: v })}
                      style={{ width: "100%", height: 40, marginTop: 4 }}
                    >
                      <Option value={CashFlowItemType.RECURRENT}>Récurrent (mensuel)</Option>
                      <Option value={CashFlowItemType.PONCTUEL}>Ponctuel (une fois)</Option>
                    </Select>
                  </Col>

                  <Col span={12}>
                    {createFormData.type === CashFlowItemType.RECURRENT ? (
                      <>
                        <Text strong style={{ color: "#64748b", fontSize: 12, textTransform: "uppercase" }}>Jour de prélèvement</Text>
                        <InputNumber
                          min={1} max={31}
                          value={createFormData.jourDuMois}
                          onChange={(v) => setCreateFormData({ ...createFormData, jourDuMois: v || 1 })}
                          style={{ width: "100%", height: 40, marginTop: 4 }}
                        />
                      </>
                    ) : (
                      <>
                        <Text strong style={{ color: "#64748b", fontSize: 12, textTransform: "uppercase" }}>Date d'échéance</Text>
                        <DatePicker
                          value={dayjs(createFormData.echeance)}
                          onChange={(date) => setCreateFormData({ ...createFormData, echeance: date ? date.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD") })}
                          disabledDate={(curr) => curr && curr < dayjs().startOf("day")}
                          style={{ width: "100%", height: 40, marginTop: 4 }}
                          format="DD/MM/YYYY"
                        />
                      </>
                    )}
                  </Col>

                  <Col span={24}>
                    <Text strong style={{ color: "#64748b", fontSize: 12, textTransform: "uppercase" }}>Catégorie</Text>
                    <Input
                      value={createFormData.category.replace(/_/g, " ").toUpperCase()}
                      disabled
                      style={{ width: "100%", height: 40, marginTop: 4, background: "#f8fafc" }}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </Modal>
        </>
      )}
    </div>
  );
};

export default Finance;
